import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { sqlReadRaw as sqlRead, payloadOf } from "../../utils/sqlClient";
import { computeUnitSellPrice, computeLineCost } from "../../utils/estimatePricing";
import { COST_SOURCES, resolveLineUnitCost, describeBasis, quoteLineOptions, carriedPricingPayload, catalogUnitCost } from "../../utils/carriedPricing";
import { computeRoomFormulaQty, engineVariableBindings } from "../../utils/roomFormulaEngine";
import { buildBodOptionAttrs, DEFAULT_SYSTEMS_TUNABLES, calcRoomSystems } from "../../utils/roomSystemsEngine";
import { groupElementsByRoom, resolveElementQuantity, placedCountFor, isPlaceable, quantityTrace } from "../../config/roomElements";
import { backOutsForElement } from "../../utils/takeoffExclusions";
import { openingsForRoom } from "../../config/openings";
import { utilityDistributionRollup, utilityCoverage } from "../../utils/utilityPricing";
import { buildScenarioModel, scenarioEquipmentDispositions, evaluateScenarios, nonBaseEquipmentIds } from "../../utils/designScenarios";
import { scenarioEstimateRows, scenarioDistributionRows } from "../../utils/scenarioCosting";
import { mergeDrawnAndElementRows } from "../../utils/estimateRollupMerge";
import { summarizeUnreviewed, describeUnreviewed } from "../../utils/machineReview";
import { computeConditionQuantities } from "../../config/conditionCalcTypes";
import { rollupUtilityDemand } from "../../utils/utilityDemand";
import { useTakeoffLiveLink } from "../../utils/takeoffLiveLink";
import { evaluateUtilityRoutes, evaluateHvacRoutes } from "../../utils/pipeSizing";
import { computeAhuZoneRollup } from "../../utils/hvacLoadEngine";
import { equipmentHeatGainByRoom } from "../../utils/equipmentLoads";
import { UTILITY_TYPES } from "../../config/utilityTypes";
import { appQuantity } from "../../config/scopeOptions";
import { roundForPricing } from "../../utils/quantityRounding";
import SubstituteItemDialog from "./SubstituteItemDialog";
import ContextMenu from "../../components/ContextMenu";
import { takeoffChannelName } from "../../utils/takeoffLiveLink";
import { ItemAssemblyPicker, AddItemOrAssemblyFlyout } from "../../components/ItemAssemblyPicker";
import { romOf, lineTotal, overrideKeyOf, overrideKeyOfRow, ambiguousKeys, applyRollupOverride } from "../../utils/rollupOverrides";
import { buildEstimateRows, buildEstimateXlsx, estimateFileName } from "../../utils/estimateExport";
import { indexComponents, applyAssemblyRollups, explodeRollupRows, leafOverrideKey, leafOverrideKeyOfRow } from "../../utils/assemblyComponents";

// Estimate — one complete cost/sell rollup per revision, live and recomputed
// every time this page is viewed (nothing here is saved directly except the
// manual line items themselves). Every row carries a Source marker — "T"
// (Takeoff-mapped) or "M" (Manual) — rather than living in a separate tab,
// since at the end of the day it's one estimate with one grand total. Four
// source types feed it, merged into one table:
//   - Kind-based self-pricing (Phase D-1 of consolidating Condition Library
//     into Items/Assemblies — see project memory) — server-side,
//     sp_GetEstimateDesignRollup (db/49): a drawn/placed instance already
//     names its own Item/Assembly directly, matched and priced live. The
//     legacy 'condition_type' mapping mechanism this originally replaced
//     was fully retired (Phase D-3) once nothing referenced it anymore.
//   - 'room_formula' (Phase 2) — computed client-side below (roomFormulaRollup),
//     not in SQL: it evaluates an admin-editable EstimateCostFormulas
//     expression (formulaEngine.js) per room, and there's no T-SQL
//     equivalent of that safe evaluator anywhere in this codebase. See
//     db/49's header comment for the full rationale.
//   - ScopeApplications (Phase E, see project memory) — computed client-side
//     below (scopeApplicationRollup), same reasoning as room_formula: the
//     per-room-subset targeting math already lives in scopeOptions.js, no
//     reason to duplicate it in T-SQL.
//   - Room Elements (Room Build-Up, db/70, 2026-09-11) — computed
//     client-side below (roomElementsRollup): each RoomElements row is the
//     estimator's "this room has X" decision naming a catalog Item/Assembly;
//     src/config/roomElements.js resolves its quantity (room geometry, the
//     HEPA/lighting engine, a fixed qty, or the placed symbol count) and the
//     catalog says what it costs. A PLACEABLE element's placed symbols
//     already price through the design rollup above, so only its unplaced
//     remainder (required − placed) is priced here — total = required.
//     (Room Template Standard Equipment used to price directly from here;
//     templates now SEED elements instead, so this is the single path.)
//   - Manual (Source "M") — EstimateCostLineItems (already cross-trade — see
//     its Trade column, db/30), for scope that doesn't fit a takeoff-mapped
//     pattern (switchgear, testing, temp power, general conditions), priced
//     the same way EstimateLineItemsPanel used to (computeLineCost) and
//     editable inline via the "+ Add to Estimate" form below the table.

const TRADES = ["electrical", "mechanical", "architectural", "general"];
const emptyLineItemDraft = { LineItemID: null, Kind: "assembly", ItemID: null, AssemblyID: null, Trade: "general", Description: "", Qty: 1, LaborFactorID: null, Notes: "", AlternateID: null,
  CostSource: "catalog", QuoteID: null, QuoteLineItemID: null, SourceUnitCost: null, CarriedUnitCost: null, CarriedNote: "",
  UtilityType: null }; // Phase H: set = this line is the distribution allowance for that utility system

// The estimate worksheet's look: slate-blue header with white bold text, pale blue-grey category bands.
// The worksheet's typeface; Franklin Gothic Book ships with Windows / Office, a close sans-serif stands in elsewhere.
const SHEET_FONT = '"Franklin Gothic Book", "Libre Franklin", "Franklin Gothic Medium", Arial, sans-serif';
const SHEET_HEAD = "#44546a", SHEET_BAND = "#d6dce4", SHEET_BAND_TEXT = "#1f2937";
const th = { textAlign: "center", fontSize: 11, fontWeight: 700, color: "#ffffff", background: SHEET_HEAD, padding: "7px 8px", borderBottom: "1px solid var(--cs-border)", borderRight: "1px solid rgba(255,255,255,0.18)", whiteSpace: "nowrap" };
const thR = th; // headings are centered over their columns, as on the worksheet
const tdR = { textAlign: "right" };
// $/SF = a Total divided by the project's square footage (Bid Package ▸ Project SF); null without one.
const perSf = (total, sf) => (Number(sf) > 0 && total != null ? total / Number(sf) : null);
const bandCell = { background: SHEET_BAND, color: SHEET_BAND_TEXT, fontWeight: 700 };
const td = { padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12 };
const fmt = (n) => n == null || isNaN(n) ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
// ROM = Total rounded UP to the next $100 (Excel CEILING(total,100)) — whole dollars, so no cents.

// Accounting format, as in the worksheet: "$" pinned to the left edge of the cell, digits flush right,
// zero shown as "-", a credit in parentheses.
function Acct({ v, dec = 2 }) {
  const n = Number(v);
  if (v == null || isNaN(n)) return <span style={{ display: "block", textAlign: "right" }}>—</span>;
  const abs = Math.abs(n);
  const zero = Math.round(abs * 10 ** dec) === 0;
  const text = zero ? "-" : `${n < 0 ? "(" : ""}${abs.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec })}${n < 0 ? ")" : ""}`;
  return <span style={{ display: "flex", justifyContent: "space-between", gap: 8, fontVariantNumeric: "tabular-nums" }}><span>$</span><span>{text}</span></span>;
}

// The line's Description, editable. For a takeoff line it is THIS estimate's name for the catalog type
// (TakeoffConditionOverrides.NameOverride) — the same name the takeoff canvas, dock, legend and exports show.
function EditText({ value, onCommit, disabled, overridden, title }) {
  const [draft, setDraft] = useState(null);
  if (disabled) return <span title={title}>{value}</span>;
  const commit = () => {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    if (next === (value ?? "")) return;
    onCommit(next === "" ? null : next);
  };
  return (
    <input value={draft ?? value ?? ""} onChange={e => setDraft(e.target.value)} onBlur={commit} title={title || "Renames this line here and in the takeoff — this estimate only"}
      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); else if (e.key === "Escape") { setDraft(null); e.currentTarget.blur(); } }}
      style={{ width: "100%", minWidth: 180, fontSize: 12, padding: "2px 4px", border: "1px solid var(--cs-border)", borderRadius: 4, background: overridden ? "var(--tint-amber)" : "var(--surface)", color: "inherit" }} />
  );
}

// A number the estimator can type over. Commits on blur / Enter (Esc reverts); an empty box clears
// the override and the line goes back to its computed value. Overridden cells are tinted.
function EditCell({ value, display, onCommit, disabled, overridden, suffix = "", placeholder = "", width = 78, title, money = false }) {
  const raw = value == null ? "" : String(Math.round(value * 10000) / 10000);
  // Shown with thousands commas (and cents on money) until the box is focused; typing is plain digits, commas are ignored on commit.
  const shown = value == null || isNaN(value) ? "" : Number(value).toLocaleString("en-US", { minimumFractionDigits: money ? 2 : 0, maximumFractionDigits: money ? 2 : 4 });
  const [draft, setDraft] = useState(null);
  if (disabled) return <span title={title} style={{ display: "block", textAlign: "right" }}>{money ? <Acct v={value} /> : display}</span>;
  const commit = () => {
    if (draft === null) return;
    const t = draft.replace(/[,$\s]/g, "");
    const next = t === "" ? null : Number(t);
    setDraft(null);
    if (next !== null && !Number.isFinite(next)) return;
    if (next === null ? !overridden : next === value) return; // nothing to clear / nothing changed
    onCommit(next);
  };
  return (
    <span style={{ whiteSpace: "nowrap", display: money ? "flex" : "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }} title={title}>
      {money && <span>$</span>}
      <input
        type="text" inputMode="decimal" value={draft ?? shown} placeholder={placeholder}
        onFocus={e => { setDraft(raw); const el = e.currentTarget; setTimeout(() => el.select(), 0); }}
        onChange={e => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); else if (e.key === "Escape") { setDraft(null); e.currentTarget.blur(); } }}
        style={{ width, boxSizing: "border-box", fontSize: 12, padding: "3px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", border: "1px solid var(--cs-border)", borderRadius: 4, background: overridden ? "var(--tint-amber)" : "var(--surface)", color: "inherit" }}
      />{suffix && <span style={{ display: "inline-block", width: 12, textAlign: "left" }}>{suffix}</span>}
    </span>
  );
}

// Design Rollup grouping — same collapsible-group shape as
// EstimateCostLibrary.jsx's groupItemsByCostCode, keyed by Category instead
// of Cost Code. A row whose joined entity has no CategoryID falls into an
// "Uncategorized" bucket, matching that same fallback convention.
function groupRollupByCategory(rows, categories) {
  const groups = [];
  const matched = new Set();
  categories.forEach(cat => {
    const groupRows = rows.filter(r => r.CategoryID === cat.CategoryID);
    if (groupRows.length) {
      groups.push({ key: String(cat.CategoryID), label: cat.Name, rows: groupRows });
      matched.add(cat.CategoryID);
    }
  });
  const uncategorized = rows.filter(r => !matched.has(r.CategoryID));
  if (uncategorized.length) groups.push({ key: "uncategorized", label: "Uncategorized", rows: uncategorized });
  return groups;
}

// Alternates grouping — same shape as groupRollupByCategory, keyed by
// AlternateID instead. A row whose AlternateID doesn't match any active
// alternate (e.g. the alternate was retired after the row was assigned)
// falls into a fallback bucket rather than silently vanishing.
function groupRollupByAlternate(rows, alternates) {
  const groups = [];
  const matched = new Set();
  alternates.forEach(alt => {
    const groupRows = rows.filter(r => r.AlternateID === alt.AlternateID);
    if (groupRows.length) {
      groups.push({ key: String(alt.AlternateID), label: alt.Name, rows: groupRows });
      matched.add(alt.AlternateID);
    }
  });
  const orphaned = rows.filter(r => !matched.has(r.AlternateID));
  if (orphaned.length) groups.push({ key: "unknown-alternate", label: "Unknown Alternate", rows: orphaned });
  return groups;
}

// The drill-down under a split assembly line: the estimator's own nesting (sub-assemblies indented, with their totals),
// each Item with its quantity, unit cost, cost and price. Detail for the estimator — the line above it is what is reported.
function ComponentTree({ nodes, parentLabel, canEdit = false, onEditLeaf }) {
  const cell = { padding: "2px 8px", fontSize: 11.5, borderBottom: "1px solid var(--cs-border)" };
  const rows = [];
  const walk = (list, depth) => list.forEach((n, i) => {
    rows.push({ n, depth, key: `${depth}:${i}:${n.code}` });
    if (n.children?.length) walk(n.children, depth + 1);
  });
  walk(nodes, 0);
  const heads = ["Component", "Qty", "Unit", "Unit cost", "Cost", "Burden %", "Markup %", "Unit price", "Price", "Quantity basis"];
  const leftHeads = ["Component", "Unit", "Quantity basis"];
  return (
    <div data-testid="component-tree" style={{ marginLeft: 24 }}>
      <div style={{ fontWeight: 700, fontSize: 11.5, marginBottom: 2 }}>Components of {parentLabel}{canEdit && <span style={{ fontWeight: 400, color: "var(--text3)" }}> — type over a quantity, cost, burden, markup or price for this estimate only</span>}</div>
      <table style={{ borderCollapse: "collapse" }}>
        <thead><tr>{heads.map(h => <th key={h} style={{ ...cell, textAlign: leftHeads.includes(h) ? "left" : "right", color: "var(--text3)", fontWeight: 600 }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map(({ n, depth, key }) => {
            const edit = canEdit && !n.branch && n.childKind && onEditLeaf;
            const ov = n.overridden || {};
            const cellEdit = (field, value, display, opts = {}) => (
              <EditCell value={value} display={display} overridden={ov[field]} disabled={!edit} width={opts.width || 64} suffix={opts.suffix || ""} placeholder={opts.placeholder || ""} money={opts.money}
                onCommit={v => onEditLeaf(n, field === "unitPrice" ? { unitPrice: v, ...(v != null ? { markup: null } : {}) } : field === "markup" ? { markup: v, unitPrice: null } : { [field]: v })} />
            );
            return (
              <tr key={key} style={n.branch ? { background: "var(--surface2)", fontWeight: 600 } : undefined}>
                <td style={{ ...cell, paddingLeft: 8 + depth * 18 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "0 5px", borderRadius: 100, marginRight: 6, background: n.kind === "assembly" ? "#e0e7ff" : "#dcfce7", color: n.kind === "assembly" ? "#4338ca" : "#15803d" }}>{n.kind === "assembly" ? "ASM" : "ITEM"}</span>
                  <span style={{ fontFamily: "monospace" }}>{n.code}</span> {n.name}
                </td>
                <td style={{ ...cell, textAlign: "right" }}>{n.branch ? (+n.qty).toLocaleString(undefined, { maximumFractionDigits: 4 }) : cellEdit("qty", +n.qty, (+n.qty).toLocaleString(undefined, { maximumFractionDigits: 4 }))}</td>
                <td style={cell}>{n.uom}</td>
                <td style={{ ...cell, textAlign: "right" }}>{n.unitCost == null ? "" : cellEdit("unitCost", n.unitCost, fmt(n.unitCost), { money: true, width: 70 })}</td>
                <td style={{ ...cell, textAlign: "right" }}>{fmt(n.direct)}</td>
                <td style={{ ...cell, textAlign: "right" }}>{n.branch ? "" : cellEdit("burden", n.hasRates ? n.burdenPct : null, n.hasRates ? `${n.burdenPct}%` : "Std.", { suffix: "%", placeholder: "Std.", width: 52 })}</td>
                <td style={{ ...cell, textAlign: "right" }}>{n.branch ? "" : cellEdit("markup", n.hasRates ? n.markupPct : null, n.hasRates ? `${n.markupPct}%` : "Std.", { suffix: "%", placeholder: "Std.", width: 52 })}</td>
                <td style={{ ...cell, textAlign: "right" }}>{n.branch || n.unitPrice == null ? "" : cellEdit("unitPrice", n.unitPrice, fmt(n.unitPrice), { money: true, width: 70 })}</td>
                <td style={{ ...cell, textAlign: "right" }}>{fmt(n.sell)}</td>
                <td style={{ ...cell, color: "var(--text2)", fontFamily: n.formula ? "monospace" : "inherit" }}>{n.formula ? n.formula : "× qty per unit"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// One data row, shared by the Base Scope table and every Alternate section
// below it — same 15-column shape either way. Manual ("M") rows get a
// "Move to:" select (the drag substitute — see project memory) that can send
// a line either direction between Base Scope and any Alternate; takeoff-
// mapped ("T") rows have no LineItemID to move, but any row can be
// duplicated-and-credited into an Alternate via the row's own action.
function RollupRow({
  r, rowKey, isLocked, alternates, lineItemsById,
  onEdit, onRemove, onMove, onEditRow, onResetRow, projectSF, onSubstitute, onRowMenu,
  creditPromptKey, onToggleCreditPrompt, creditTargetAlternateId, setCreditTargetAlternateId,
  creditNewAlternateName, setCreditNewAlternateName, onConfirmCredit,
}) {
  const showCreditPrompt = creditPromptKey === rowKey;
  const [showWhy, setShowWhy] = useState(false); // Track I — per-room contributions behind a room-element row
  const [showParts, setShowParts] = useState(false); // the components behind a split assembly line (a client report shows the rolled-up line only)
  const canEdit = !isLocked && r.editable;
  const ov = r.overridden || {};
  const anyOverride = ov.qty || ov.unitCost || ov.burden || ov.markup || ov.unitPrice || r.descOverridden || r.AnyLeafOverridden;
  const lockedWhy = isLocked ? "Locked revision" : r.editBlockedReason;
  // Right-click menu: the same actions as the last column's links, in one place.
  const substKind = r.Exploded ? r.ParentKind : r.EntityKind, substId = r.Exploded ? r.ParentID : r.EntityID;
  const canSubstitute = !!onSubstitute && ["item", "assembly"].includes(substKind) && substId != null;
  const menuItems = () => {
    const items = [{ heading: r.Description || r.Code || "Line" }];
    if (canSubstitute) items.push({ label: r.Exploded ? "Substitute assembly…" : "Substitute…", disabled: isLocked, why: isLocked ? "Locked revision" : undefined, onClick: () => onSubstitute(r) });
    if (r.Tree?.length > 0) items.push({ label: showParts ? "Hide components" : "Show components", onClick: () => setShowParts(w => !w) });
    if (r.Contributions?.length > 0) items.push({ label: showWhy ? "Hide why" : "Why this quantity?", onClick: () => setShowWhy(w => !w) });
    items.push({ label: "Reset to computed values", disabled: isLocked || !anyOverride, why: isLocked ? "Locked revision" : !anyOverride ? "Nothing typed over on this line" : undefined, onClick: () => onResetRow(r) });
    if (r.Source === "M") {
      items.push({ separator: true });
      items.push({ label: "Edit line…", disabled: isLocked, onClick: () => onEdit(lineItemsById.get(r.LineItemID)) });
      items.push({ label: "Move to Base Scope", disabled: isLocked || r.AlternateID == null, why: r.AlternateID == null ? "Already in Base Scope" : undefined, onClick: () => onMove(lineItemsById.get(r.LineItemID), null) });
      alternates.filter(a => a.AlternateID !== r.AlternateID).forEach(a => items.push({ label: `Move to ${a.Name}`, disabled: isLocked, onClick: () => onMove(lineItemsById.get(r.LineItemID), a.AlternateID) }));
    }
    if (!r.Exploded) {
      items.push({ separator: true });
      items.push({ label: "Credit to Alternate…", disabled: isLocked, why: isLocked ? "Locked revision" : undefined, onClick: () => onToggleCreditPrompt(rowKey) });
    }
    if (r.Source === "M") items.push({ label: "Remove line", danger: true, disabled: isLocked, onClick: () => onRemove(r.LineItemID) });
    return items;
  };
  return (
    <Fragment>
      <tr onContextMenu={e => { if (e.target.closest?.("input, textarea, select")) return; e.preventDefault(); onRowMenu?.({ x: e.clientX, y: e.clientY, items: menuItems() }); }}>
        <td style={{ ...td, fontFamily: "monospace", whiteSpace: "nowrap" }} title={r.CostCodeDescription || ""}>{r.CSICode || "—"}</td>
        <td style={{ ...td, textAlign: "center", fontWeight: 700, color: r.Source === "M" ? "var(--cs-blue)" : "var(--text3)" }} title={r.Source === "M" ? "Manual line item" : "Takeoff-mapped"}>{r.Source}</td>
        <td style={td}>{r.Line}</td>
        <td style={{ ...td, fontFamily: "monospace" }}>{r.Code || "—"}</td>
        <td style={td}><EditText value={r.Description} overridden={r.descOverridden} disabled={isLocked || !r.canRename} onCommit={v => onEditRow(r, { description: v })} /></td>
        <td style={{ ...td, ...tdR, whiteSpace: "nowrap" }}>
          <EditCell value={+r.TotalQty} overridden={ov.qty} disabled={!canEdit} title={lockedWhy}
            display={(+r.TotalQty).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            onCommit={v => onEditRow(r, { qty: v })} />
          <span style={{ display: "inline-block", width: 34, textAlign: "left", marginLeft: 4, color: "var(--text3)" }}>{r.MatchedInstanceCount > 1 ? `(${r.MatchedInstanceCount})` : ""}</span>
        </td>
        <td style={td}>{r.UOM}</td>
        <td style={td} title={r.basisText || (r.Source === "M" ? "Catalog cost (live)" : "Catalog cost")}>
          <EditCell money value={r.unitCost} overridden={ov.unitCost || r.carried} disabled={!canEdit} title={lockedWhy} display={fmt(r.unitCost)}
            onCommit={v => onEditRow(r, { unitCost: v })} />
          {r.carried && <span style={{ marginLeft: 4, fontSize: 9.5, fontWeight: 700, padding: "0 5px", borderRadius: 100, background: r.costSource === "quote" ? "#d1fae5" : "#f3f4f6", color: r.costSource === "quote" ? "#065f46" : "#374151" }}>{r.costSource === "quote" ? "Q" : "M"}{r.basisStale ? "!" : ""}</span>}
        </td>
        <td style={td}><Acct v={r.DirectCost} /></td>
        <td style={{ ...td, ...tdR }}>
          <EditCell value={r.hasPerLineRates ? r.salesBurdenPct : null} overridden={ov.burden} disabled={!canEdit} title={lockedWhy} width={56} suffix="%" placeholder="Std."
            display={r.hasPerLineRates ? `${r.salesBurdenPct}%` : "Std."} onCommit={v => onEditRow(r, { burden: v })} />
        </td>
        <td style={td}>{r.hasPerLineRates ? <Acct v={r.totalSalesBurden} /> : <span style={{ display: "block", textAlign: "right" }}>—</span>}</td>
        <td style={td}><Acct v={r.totalCost} /></td>
        <td style={{ ...td, ...tdR }}>
          <EditCell value={r.hasPerLineRates ? r.markupPct : null} overridden={ov.markup || ov.unitPrice} disabled={!canEdit} title={lockedWhy} width={56} suffix="%" placeholder="Std."
            display={r.hasPerLineRates ? `${r.markupPct}%` : "Std."} onCommit={v => onEditRow(r, { markup: v, unitPrice: null })} />
        </td>
        <td style={td} title="Type a price and the markup is back-calculated">
          <EditCell money value={r.unitPrice} overridden={ov.unitPrice} disabled={!canEdit} title={lockedWhy} display={fmt(r.unitPrice)}
            onCommit={v => onEditRow(r, { unitPrice: v, ...(v != null ? { markup: null } : {}) })} />
        </td>
        <td style={{ ...td, fontWeight: 600 }}><Acct v={r.SellValue} dec={2} /></td>
        <td style={{ ...td, fontWeight: 600, color: "var(--text2)" }} title="Total rounded up to the next $100"><Acct v={romOf(r.SellValue)} /></td>
        <td style={{ ...td, color: "var(--text2)" }} title="Total ÷ project SF"><Acct v={perSf(r.SellValue, projectSF)} /></td>
        <td style={td}>
          {r.Tree?.length > 0 && (
            <span onClick={() => setShowParts(w => !w)} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }} title="Every component behind this line: items and sub-assemblies, with quantities, cost and price">{showParts ? "hide components" : "components"}</span>
          )}
          {r.Contributions?.length > 0 && (
            <span onClick={() => setShowWhy(w => !w)} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }} title="Which rooms this quantity comes from, and why each is what it is">{showWhy ? "hide why" : "why?"}</span>
          )}
          {!isLocked && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
              {anyOverride && <span onClick={() => onResetRow(r)} style={{ cursor: "pointer", color: "var(--cs-blue)" }} title="Clear the numbers you typed on this line and go back to the computed values">Reset</span>}
              {r.Source === "M" && (
                <>
                  <span onClick={() => onEdit(lineItemsById.get(r.LineItemID))} style={{ cursor: "pointer", color: "var(--cs-blue)" }}>Edit</span>
                  <span onClick={() => onRemove(r.LineItemID)} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                  <select
                    value={r.AlternateID ?? ""}
                    onChange={e => onMove(lineItemsById.get(r.LineItemID), e.target.value ? +e.target.value : null)}
                    style={{ fontSize: 11, padding: "1px 3px", border: "1px solid var(--cs-border)", borderRadius: 3 }}
                  >
                    <option value="">Base Scope</option>
                    {alternates.map(a => <option key={a.AlternateID} value={a.AlternateID}>{a.Name}</option>)}
                  </select>
                </>
              )}
              {!r.Exploded && <span onClick={() => onToggleCreditPrompt(rowKey)} style={{ cursor: "pointer", color: "var(--text3)" }}>Credit to Alt…</span>}
            </div>
          )}
        </td>
      </tr>
      {showParts && r.Tree?.length > 0 && (
        <tr>
          <td style={{ ...td, background: "var(--surface)", whiteSpace: "normal" }} colSpan={18}>
            <ComponentTree nodes={r.Tree} parentLabel={r.Description} canEdit={canEdit || (!isLocked && !!r.Tree)} onEditLeaf={(node, patch) => onEditRow(r, patch, node)} />
          </td>
        </tr>
      )}
      {showWhy && r.Contributions?.length > 0 && (
        <tr>
          <td style={{ ...td, background: "var(--surface)", whiteSpace: "normal" }} colSpan={18}>
            <div style={{ fontSize: 11.5 }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Why {(+r.TotalQty).toLocaleString(undefined, { maximumFractionDigits: 2 })} {r.UOM}? — {r.Contributions.length} source{r.Contributions.length === 1 ? "" : "s"}</div>
              {r.Contributions.map(c => (
                <div key={c.elementId} style={{ display: "flex", gap: 10, padding: "1px 0" }}>
                  <span style={{ minWidth: 160, fontWeight: 600 }}>{c.roomNumber ? `${c.roomNumber} · ` : ""}{c.roomName}</span>
                  <span style={{ color: "var(--text2)" }}>{c.summary}</span>
                </div>
              ))}
              <div style={{ color: "var(--text3)", marginTop: 2 }}>Full step-by-step trace and geometry highlight: open the room on Takeoff &amp; Design and use "Why this quantity?" on the element card.</div>
            </div>
          </td>
        </tr>
      )}
      {showCreditPrompt && (
        <tr>
          <td style={{ ...td, background: "var(--surface)" }} colSpan={18}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <span style={{ color: "var(--text3)" }}>Credit "{r.Description}" to:</span>
              <select value={creditTargetAlternateId} onChange={e => setCreditTargetAlternateId(e.target.value)} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 }}>
                <option value="">— pick an existing alternate —</option>
                {alternates.map(a => <option key={a.AlternateID} value={a.AlternateID}>{a.Name}</option>)}
              </select>
              <span style={{ color: "var(--text3)" }}>or new:</span>
              <input placeholder="New alternate name" value={creditNewAlternateName} onChange={e => setCreditNewAlternateName(e.target.value)} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 }} />
              <button onClick={() => onConfirmCredit(r)} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>Confirm</button>
              <button onClick={() => onToggleCreditPrompt(null)} style={{ fontSize: 12 }}>Cancel</button>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export default function Estimate({ projectId, projectName = "" }) {
  const [revisions, setRevisions]     = useState([]);
  const [activeRevId, setActiveRevId] = useState(null);
  const [loading, setLoading]         = useState(true);

  const [rollup, setRollup]             = useState([]);
  const [rollupLoading, setRollupLoading] = useState(false);
  const [categories, setCategories]     = useState([]);
  const [assembliesRaw, setAssemblies]   = useState([]);
  const [items, setItems]               = useState([]);
  const [laborFactors, setLaborFactors] = useState([]);
  const [formulas, setFormulas]         = useState([]);
  const [lineItems, setLineItems]       = useState([]);
  const [settings, setSettings]         = useState(null);
  const [rooms, setRooms]               = useState([]);
  const [zones, setZones] = useState([]);
  const [criteria, setCriteria]         = useState({});
  const [roomTemplateCriteria, setRoomTemplateCriteria] = useState([]); // Room Templates catalog (global) — see db/55_room_templates.sql
  const [roomElements, setRoomElements] = useState([]);           // Room Build-Up elements (db/70) — roomElementsRollup + engine gating
  const [takeoffConditions, setTakeoffConditions] = useState([]); // placed symbols — a placeable element prices only its unplaced remainder here
  const [bodOptionRows, setBodOptionRows] = useState([]);           // Basis of Design option rows (global) — filter/fixture engineering attributes for roomSystemsEngine.js
  const [takeoffVariableRows, setTakeoffVariableRows] = useState([]); // Takeoff Variables (global) — engine design assumptions (db/68)
  const [scopeApplications, setScopeApplications] = useState([]); // ScopeApplications for scopeApplicationRollup (Phase E — see project memory)
  const [scopeAppEntities, setScopeAppEntities]   = useState({}); // ScopeApplicationID -> resolved catalog entity (sp_GetScopeApplicationEntities)
  // Assemblies built from components (db/88). `assemblies` is the catalog list with every component-built assembly
  // carrying its rolled-up cost, so each flat consumer below prices it from its components; the Estimate lines of such
  // an assembly are then split per cost code (explodeRollupRows, in combinedRollup).
  const [assemblyComponentRows, setAssemblyComponentRows] = useState([]);
  const componentsByAssembly = useMemo(() => indexComponents(assemblyComponentRows), [assemblyComponentRows]);
  const componentItemsById = useMemo(() => new Map(items.map(i => [i.ItemID, i])), [items]);
  // Engine Variables a component quantity formula can use by name (next to `qty`) — from the rows directly, not systemsCtx (declared later).
  const assemblyEngineVars = useMemo(() => engineVariableBindings(Object.fromEntries(takeoffVariableRows.map(r => [r.VariableKey, +r.Value]))), [takeoffVariableRows]);
  const assemblies = useMemo(
    () => applyAssemblyRollups(assembliesRaw, { componentsByAssembly, itemsById: componentItemsById, engineVars: assemblyEngineVars, libraryFormulas: formulas }, new Map(laborFactors.map(f => [f.FactorID, f]))),
    [assembliesRaw, componentsByAssembly, componentItemsById, laborFactors, assemblyEngineVars, formulas]);
  const [collapsedRollupGroups, setCollapsedRollupGroups] = useState(new Set());
  const toggleRollupGroup = (key) => {
    setCollapsedRollupGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const [substituteRow, setSubstituteRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // right-click menu on an Estimate line
  const toggleCreditPrompt = (key) => {
    setCreditPromptKey(prev => (prev === key ? null : key));
    setCreditTargetAlternateId("");
    setCreditNewAlternateName("");
  };

  // Manual line item add/edit (ported from the former EstimateLineItemsPanel,
  // now inlined so its rows can interleave with the takeoff-mapped rollup in
  // one table).
  const [lineItemDraft, setLineItemDraft]   = useState(null);
  // P3-C: this project's quotes + lines, the evidence a manual line may carry
  // its price from (sp_GetProjectQuotes, db/71). Project-level, loaded once.
  const [projectSF, setProjectSF] = useState(null); // Bid Package ▸ Project SF — the divisor of the $/SF column
  useEffect(() => {
    if (!projectId) return;
    sqlRead(`EXEC sp_GetBidPackage @project_id=${+projectId}`)
      .then(result => setProjectSF(Number(result?.Table1?.[0]?.ProjectSF) || null))
      .catch(e => console.error("Project SF load failed ($/SF column empty):", e));
  }, [projectId]);
  const [projectQuotes, setProjectQuotes] = useState({ quotes: [], lines: [] });
  useEffect(() => {
    if (!projectId) return;
    sqlRead(`EXEC sp_GetProjectQuotes @project_id=${+projectId}`)
      .then(result => setProjectQuotes({ quotes: result?.Table1 || [], lines: result?.Table2 || [] }))
      .catch(e => console.error("Project quotes load failed (carried pricing picker empty):", e));
  }, [projectId]);
  const quoteLinePickerOptions = useMemo(() => quoteLineOptions(projectQuotes.quotes, projectQuotes.lines), [projectQuotes]);
  const [savingLineItem, setSavingLineItem] = useState(false);
  const [showAddNewEntity, setShowAddNewEntity] = useState(false);

  // Alternates (base scope + add/deduct options — see project memory).
  const [alternates, setAlternates]           = useState([]);
  const [alternateDraft, setAlternateDraft]   = useState(null); // { Name, Description } while creating a new alternate
  const [savingAlternate, setSavingAlternate] = useState(false);
  // "Duplicate & Credit Out" mini-form — creditPromptKey identifies which
  // row's inline picker is open (matches that row's React key).
  const [creditPromptKey, setCreditPromptKey]     = useState(null);
  // Estimator edits to takeoff-driven lines (db/30 EstimateRollupOverrides), by overrideKeyOf().
  const [rollupOverrides, setRollupOverrides] = useState(new Map());
  // the estimator's edits to individual COMPONENT lines of a component-built assembly (db/47) — same shape as rollupOverrides
  const [assemblyLineOverrides, setAssemblyLineOverrides] = useState(new Map());
  const [nameOverrides, setNameOverrides] = useState(new Map()); // `${kind}:${id}` -> this estimate's name for the catalog type (TakeoffConditionOverrides)
  const [creditTargetAlternateId, setCreditTargetAlternateId] = useState("");
  const [creditNewAlternateName, setCreditNewAlternateName]   = useState("");

  useEffect(() => {
    setLoading(true);
    sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`)
      .then(result => {
        const revs = result?.Table1 || result?.value || [];
        setRevisions(revs);
        const active = revs.find(r => !r.IsLocked) || revs[0];
        if (active) setActiveRevId(active.RevisionID);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const loadRollup = useCallback(() => {
    if (!activeRevId) return;
    setRollupLoading(true);
    sqlRead(`EXEC sp_GetEstimateDesignRollup @revision_id=${activeRevId}`)
      .then(result => setRollup(result?.Table1 || []))
      .catch(e => console.error("Design rollup load failed:", e))
      .finally(() => setRollupLoading(false));
  }, [activeRevId]);

  const loadLibrary = useCallback(() => {
    // Passing the revision lets THIS estimate's own one-off conditions (db/87) resolve alongside the catalog.
    (activeRevId ? sqlRead(`EXEC sp_GetEstimateCostLibrary @revision_id=${activeRevId}`).catch(() => sqlRead(`EXEC sp_GetEstimateCostLibrary`)) : sqlRead(`EXEC sp_GetEstimateCostLibrary`))
      .then(result => {
        setCategories(result?.Table1 || []);
        setAssemblies(result?.Table2 || []);
        setItems(result?.Table3 || []);
        setLaborFactors(result?.Table4 || []);
        setFormulas(result?.Table5 || []);
      })
      .catch(e => console.error("Estimate library load failed:", e));
    sqlRead("EXEC sp_GetEstimateAssemblyComponents")
      .then(result => setAssemblyComponentRows(result?.Table1 || []))
      .catch(() => setAssemblyComponentRows([])); // db/88 not deployed yet = every assembly stays a flat row
  }, [activeRevId]);

  // Optimistic append for AddItemOrAssemblyFlyout (opened from the manual
  // line item picker below) — items/assemblies are owned here, so a
  // background loadLibrary() call reconciles the optimistic row with the
  // real one once it lands.
  const handleEntityCreated = useCallback((e) => {
    if (e.kind === "item") setItems(prev => [...prev, e.raw]);
    else setAssemblies(prev => [...prev, e.raw]);
    loadLibrary();
  }, [loadLibrary]);

  const loadLineItems = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEstimateCostLineItems @revision_id=${activeRevId}`)
      .then(result => setLineItems(result?.Table1 || []))
      .catch(e => console.error("Estimate line items load failed:", e));
  }, [activeRevId]);

  const loadAlternates = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEstimateLineItemAlternates @revision_id=${activeRevId}`)
      .then(result => setAlternates(result?.Table1 || []))
      .catch(e => console.error("Estimate alternates load failed:", e));
  }, [activeRevId]);

  const loadSettings = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEstimateCostSettings @revision_id=${activeRevId}`)
      .then(result => setSettings((result?.Table1 || [])[0] || null))
      .catch(e => console.error("Estimate settings load failed:", e));
  }, [activeRevId]);

  // Feeds the client-side room-formula rollup below — same shape ElectricalEstimate.jsx's
  // own loadTakeoff already fetches (Table3 rooms, Table6 flat criteria map).
  const loadRoomsAndCriteria = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetRoomTakeoff @revision_id=${activeRevId}`)
      .then(result => {
        setZones(result?.Table2 || []); // Scenarios Phase J-2: the AHU zones the scenario systems serve
        setRooms(result?.Table3 || []);
        const cr = {};
        for (const c of result?.Table6 || []) cr[`${c.Discipline}|${c.FieldKey}|${c.RoomID ?? 0}`] = c.FieldValue;
        setCriteria(cr);
      })
      .catch(e => console.error("Room takeoff load failed (room-formula rollup unavailable):", e));
  }, [activeRevId]);

  // Global (not revision-scoped) — feeds each room's _templateDefaults (see
  // roomsWithTemplateDefaults below), effectiveCriterion()'s room-template
  // fallback tier, same convention as RoomTakeoff.jsx/ElectricalEstimate.jsx.
  const loadRoomTemplates = useCallback(() => {
    sqlRead("EXEC sp_GetRoomTemplates")
      .then(result => setRoomTemplateCriteria(result?.Table2 || []))
      .catch(e => console.error("Room Templates load failed (template defaults unavailable):", e));
  }, []);

  // Room Build-Up elements + placed symbols (see roomElementsRollup below).
  // Process Utilities Phase H — equipment connections + utility system records
  // (db/24) so utility connections price here and the coverage gaps show.
  // Scenarios Phase J — the design scenarios decide which plant records price in
  // Base Scope (the selected base's + scenario-independent) and which price as a
  // proposal alternate (scenarioEstimateRows below).
  const [equipmentRegister, setEquipmentRegister] = useState({ equipment: [], connections: [], utilitySystems: [], routeSegments: [], scenarios: [], systems: [], systemZones: [] });
  const [sizingConfig, setSizingConfig] = useState(null); // db/93 — the Phase G routes' effective sizing rule (Phase H-3 prices the route takeoff)
  useEffect(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEquipmentRegister @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, equipment: result?.Table1 || [], connections: result?.Table2 || [] })))
      .catch(e => console.error("Equipment register load failed (utility connections not priced):", e));
    sqlRead(`EXEC sp_GetUtilitySystems @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, utilitySystems: result?.Table1 || [] })))
      .catch(e => console.error("Utility systems load failed (coverage uses defaults):", e));
    sqlRead(`EXEC sp_GetUtilityRouteSegments @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, routeSegments: result?.Table1 || [] })))
      .catch(e => console.error("Utility route segments load failed (distribution not priced from routes):", e));
    sqlRead(`EXEC sp_GetDesignScenarios @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, scenarios: result?.Table1 || [], systems: result?.Table2 || [], systemZones: result?.Table3 || [] })))
      .catch(e => console.error("Design scenarios load failed (estimate is scenario-blind):", e));
  }, [activeRevId]);
  useEffect(() => {
    sqlRead("EXEC sp_GetUtilitySizingConfig")
      .then(result => setSizingConfig({ standards: result?.Table1 || [], materials: result?.Table2 || [], sizes: result?.Table3 || [] }))
      .catch(e => console.error("Utility sizing configuration load failed (routes read Sizing Required):", e));
  }, []);

  const loadRoomElements = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetRoomElements @revision_id=${activeRevId}`)
      .then(result => setRoomElements(result?.Table1 || []))
      .catch(e => console.error("Room elements load failed (element rollup unavailable):", e));
    sqlRead(`EXEC sp_GetTakeoffConditionsDrawn @revision_id=${activeRevId}`)
      .then(result => setTakeoffConditions(result?.Table1 || []))
      .catch(e => console.error("Takeoff conditions load failed (placed counts unavailable):", e));
  }, [activeRevId]);

  // Global reference data the engineering engine (roomSystemsEngine.js)
  // reads — the selected filter/fixture option's airflow/lumen attributes
  // and the CU/LLF/default-airflow assumptions. Loaded once, same
  // convention as RoomTakeoff.jsx.
  useEffect(() => {
    sqlRead("EXEC sp_GetBasisOfDesignOptions")
      .then(result => setBodOptionRows(result?.Table1 || []))
      .catch(e => console.error("Basis of Design options load failed (engineering engine uses defaults):", e));
    sqlRead("EXEC sp_GetTakeoffVariables")
      .then(result => setTakeoffVariableRows(result?.Table1 || []))
      .catch(e => console.error("Takeoff variables load failed (engineering engine uses defaults):", e));
  }, []);
  const systemsCtx = useMemo(() => ({
    bodAttrs: buildBodOptionAttrs(bodOptionRows),
    settings,
    tunables: { ...DEFAULT_SYSTEMS_TUNABLES, ...Object.fromEntries(takeoffVariableRows.map(r => [r.VariableKey, +r.Value])) },
    roomElementsByRoom: groupElementsByRoom(roomElements), // Room Build-Up gating — engines run only where an element exists
  }), [bodOptionRows, takeoffVariableRows, settings, roomElements]);

  // Feeds scopeApplicationRollup below (Phase E — see project memory).
  // sp_GetScopeRegister itself can't carry each application's Kind/ItemID
  // (see db/58_scope_application_items.sql's own header comment on why) —
  // sp_GetScopeApplicationEntities resolves them separately, merged here by
  // ScopeApplicationID, same pattern ScopeRegister.jsx already uses.
  const loadScopeApplications = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetScopeRegister @revision_id=${activeRevId}`)
      .then(result => setScopeApplications(result?.Table5 || []))
      .catch(e => console.error("Scope applications load failed (scope-placement rollup unavailable):", e));
    sqlRead(`EXEC sp_GetScopeApplicationEntities @revision_id=${activeRevId}`)
      .then(result => {
        const map = {};
        for (const ent of result?.Table1 || []) map[ent.ScopeApplicationID] = ent;
        setScopeAppEntities(map);
      })
      .catch(e => console.error("Scope application entities load failed (scope-placement rollup unavailable):", e));
  }, [activeRevId]);

  const loadOverrides = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEstimateRollupOverrides @revision_id=${activeRevId}`)
      .then(result => setRollupOverrides(new Map((result?.Table1 || []).map(o => [overrideKeyOfRow(o), o]))))
      .catch(e => console.error("Estimate overrides load failed:", e));
    sqlRead(`EXEC sp_GetEstimateAssemblyLineOverrides @revision_id=${activeRevId}`)
      .then(result => setAssemblyLineOverrides(new Map((result?.Table1 || []).map(o => [leafOverrideKeyOfRow(o), o]))))
      .catch(() => setAssemblyLineOverrides(new Map())); // db/47 not deployed yet = no component edits
    sqlRead(`EXEC sp_GetTakeoffConditionOverrides @revision_id=${activeRevId}`)
      .then(result => setNameOverrides(new Map((result?.Table1 || []).filter(o => o.NameOverride).map(o => [`${o.Kind}:${o.Kind === "item" ? o.ItemID : o.AssemblyID}`, o.NameOverride]))))
      .catch(e => console.error("Estimate name overrides load failed:", e));
  }, [activeRevId]);
  useEffect(() => { setRollupOverrides(new Map()); setAssemblyLineOverrides(new Map()); setNameOverrides(new Map()); loadOverrides(); }, [loadOverrides]);

  useEffect(() => { loadRollup(); loadLibrary(); loadLineItems(); loadAlternates(); loadSettings(); loadRoomsAndCriteria(); loadRoomTemplates(); loadScopeApplications(); loadRoomElements(); },
    [loadRollup, loadLibrary, loadLineItems, loadAlternates, loadSettings, loadRoomsAndCriteria, loadRoomTemplates, loadScopeApplications, loadRoomElements]);

  // Live with the Takeoff canvas: a takeoff change saved in another window of this browser (the canvas on the other monitor)
  // re-reads everything the takeoff drives — drawn rollup, rooms + criteria, room elements + placed symbols, scope placements —
  // so every quantity and price here recalculates without a reload. Typed-over numbers and open editors are untouched.
  const { lastUpdate: liveUpdate } = useTakeoffLiveLink(activeRevId, () => { loadRollup(); loadRoomsAndCriteria(); loadRoomElements(); loadScopeApplications(); });

  const templateDefaultsByTemplateId = useMemo(() => {
    const map = {};
    for (const c of roomTemplateCriteria) {
      (map[c.RoomTemplateID] ||= {})[`${c.Discipline}|${c.FieldKey}`] = c.FieldValue;
    }
    return map;
  }, [roomTemplateCriteria]);
  const roomsWithTemplateDefaults = useMemo(
    () => rooms.map(r => ({ ...r, _templateDefaults: r.RoomTemplateID ? (templateDefaultsByTemplateId[r.RoomTemplateID] || null) : null })),
    [rooms, templateDefaultsByTemplateId]
  );

  // Phase 2: 'room_formula'-mapped Items/Assemblies, priced by evaluating
  // their formula against every room in the revision and summing. Merged
  // with the server-side 'condition_type' rollup (`rollup`, above) for
  // display — computed here, not in sp_GetEstimateDesignRollup, because
  // formula evaluation only exists client-side in this codebase (see the
  // file header comment).
  const roomFormulaRollup = useMemo(() => {
    if (!settings || !roomsWithTemplateDefaults.length) return [];
    const formulasById = new Map(formulas.map(f => [f.FormulaID, f]));
    const laborFactorsById = new Map(laborFactors.map(f => [f.FactorID, f]));
    const mapped = [
      ...items.map(i => ({ kind: "item", entity: i })),
      ...assemblies.map(a => ({ kind: "assembly", entity: a })),
    ].filter(({ entity }) => entity.QuantitySourceType === "room_formula" && entity.QuantitySourceRef);

    const rows = [];
    for (const { kind, entity } of mapped) {
      const formula = formulasById.get(entity.QuantitySourceRef);
      if (!formula) continue;
      let totalQty = 0, matchedCount = 0;
      for (const room of roomsWithTemplateDefaults) {
        const qty = computeRoomFormulaQty(formula, room, criteria, systemsCtx);
        if (qty == null) continue;
        totalQty += qty;
        matchedCount += 1;
      }
      if (matchedCount === 0) continue;

      // Same direct-cost/sell-value math as estimatePricing.js's computeLineCost,
      // inlined against totalQty directly — NOT routed through computeLineCost
      // itself, since its quantityFor() would re-resolve quantity off the
      // entity's unrelated QuantityFormulaID (the pre-existing per-assembly
      // formula field) if one happens to be set, silently discarding totalQty.
      const factorMult = kind === "assembly" ? (laborFactorsById.get(entity.DefaultLaborFactorID)?.Multiplier ?? 1) : 1;
      const unitCost = (entity.MaterialCostPerUnit || 0)
        + (entity.LaborHrPerUnit || 0) * factorMult * (settings.LaborRate || 0)
        + (entity.VendorSubCostPerUnit || 0);
      const directCost = totalQty * unitCost;

      rows.push({
        EntityKind: kind, EntityID: kind === "item" ? entity.ItemID : entity.AssemblyID,
        Code: entity.Code, Description: kind === "item" ? entity.ItemName : entity.Description,
        UOM: entity.UOM, MatchedInstanceCount: matchedCount, TotalQty: totalQty,
        DirectCost: directCost, SellValue: totalQty * computeUnitSellPrice(unitCost, entity, settings),
      });
    }
    return rows;
  }, [items, assemblies, formulas, laborFactors, roomsWithTemplateDefaults, criteria, settings, systemsCtx]);

  // Room Build-Up elements (RoomElements, db/70) — the estimator's per-room
  // decisions, priced. Each element names a catalog Item/Assembly; its
  // quantity resolves the same way the Room Detail Flyout shows it
  // (src/config/roomElements.js: room geometry, the HEPA/lighting engine —
  // gated on the element's own presence via systemsCtx — a fixed qty, or the
  // placed count). A PLACEABLE element (Style='count') already has each
  // placed/generated symbol priced by sp_GetEstimateDesignRollup, so only
  // the unplaced remainder (required − placed) is priced here — the estimate
  // always totals to the required quantity, never double. Grouped by entity
  // so the same HEPA Item across rooms sums into one line.
  const roomElementsRollup = useMemo(() => {
    if (!settings || !roomElements.length || !roomsWithTemplateDefaults.length) return [];
    const laborFactorsById = new Map(laborFactors.map(f => [f.FactorID, f]));
    const itemsById = new Map(items.map(i => [i.ItemID, i]));
    const assembliesById = new Map(assemblies.map(a => [a.AssemblyID, a]));
    const roomsById = new Map(roomsWithTemplateDefaults.map(r => [r.RoomID, r]));
    const systemsByRoom = new Map();

    const groups = new Map();
    for (const el of roomElements) {
      const room = roomsById.get(el.RoomID);
      if (!room) continue;
      const kind = el.Kind === "item" ? "item" : "assembly";
      const entity = kind === "item" ? itemsById.get(el.ItemID) : assembliesById.get(el.AssemblyID);
      if (!entity) continue;
      if (!systemsByRoom.has(room.RoomID)) systemsByRoom.set(room.RoomID, calcRoomSystems(room, criteria, systemsCtx));
      const placed = placedCountFor(el, room.RoomID, takeoffConditions);
      // back-outs (D-2) / opening deductions (D-3): floor, ceiling and wall elements price their NET area
      const q = resolveElementQuantity({ element: el, room, systems: systemsByRoom.get(room.RoomID), placed, formulas, criteria, ctx: systemsCtx,
        backOuts: backOutsForElement(el, takeoffConditions), openings: openingsForRoom(room, takeoffConditions, roomsWithTemplateDefaults) });
      const qty = isPlaceable(el) ? Math.max(0, q.required - placed) : q.effective;
      if (!(qty > 0)) continue;
      const key = `${kind}:${kind === "item" ? entity.ItemID : entity.AssemblyID}`;
      const g = groups.get(key) || { kind, entity, totalQty: 0, matchedCount: 0, placeable: isPlaceable(el), contributions: [] };
      g.totalQty += qty;
      g.matchedCount += 1;
      // Track I — why this quantity? one line per room, from the same resolver result
      g.contributions.push({ roomId: room.RoomID, roomName: room.RoomName, roomNumber: room.RoomNumber, elementId: el.RoomElementID, qty, summary: quantityTrace(q, { element: el, room }).summary });
      groups.set(key, g);
    }

    const rows = [];
    for (const { kind, entity, totalQty, matchedCount, placeable, contributions } of groups.values()) {
      const factorMult = kind === "assembly" ? (laborFactorsById.get(entity.DefaultLaborFactorID)?.Multiplier ?? 1) : 1;
      const unitCost = (entity.MaterialCostPerUnit || 0)
        + (entity.LaborHrPerUnit || 0) * factorMult * (settings.LaborRate || 0)
        + (entity.VendorSubCostPerUnit || 0);
      rows.push({
        EntityKind: kind, EntityID: kind === "item" ? entity.ItemID : entity.AssemblyID,
        Code: entity.Code, Description: `${kind === "item" ? entity.ItemName : entity.Description} (room element${placeable ? " — not yet placed" : ""})`,
        UOM: entity.UOM, MatchedInstanceCount: matchedCount, TotalQty: totalQty,
        DirectCost: totalQty * unitCost, SellValue: totalQty * computeUnitSellPrice(unitCost, entity, settings),
        SourceDetail: "element", Contributions: contributions,
      });
    }
    return rows;
  }, [roomElements, takeoffConditions, items, assemblies, formulas, laborFactors, roomsWithTemplateDefaults, criteria, settings, systemsCtx]);

  // ScopeApplications-based placements (Phase E, see project memory) —
  // wall protection etc. — price themselves off their own resolved catalog
  // entity, using the SAME per-room-subset quantity math ScopeRegister.jsx's
  // interview already computes (scopeOptions.js's appQuantity/targetRooms),
  // not duplicated in T-SQL for the same reason roomFormulaRollup above
  // isn't. Grouped by entity so multiple placements of the same Item (e.g.
  // vinyl in two different room selections) sum into one rollup line, same
  // convention as every other rollup source.
  const scopeApplicationRollup = useMemo(() => {
    if (!settings) return [];
    const groups = new Map();
    for (const app of scopeApplications) {
      const entity = scopeAppEntities[app.ScopeApplicationID];
      if (!entity) continue;
      const { qty } = appQuantity(entity, app, rooms);
      if (!qty) continue;
      const key = `${entity.Kind}:${entity.ItemID ?? entity.AssemblyID}`;
      const g = groups.get(key) || { entity, totalQty: 0, matchedCount: 0 };
      g.totalQty += qty;
      g.matchedCount += 1;
      groups.set(key, g);
    }

    const rows = [];
    for (const { entity, totalQty, matchedCount } of groups.values()) {
      // Items have no labor-factor concept at the catalog level (matches
      // mapped_entities' own convention in db/49); Assemblies aren't used
      // for scope placement today, so a factor lookup isn't needed here.
      const unitCost = (entity.MaterialCostPerUnit || 0)
        + (entity.LaborHrPerUnit || 0) * (settings.LaborRate || 0)
        + (entity.VendorSubCostPerUnit || 0);
      const directCost = totalQty * unitCost;
      rows.push({
        EntityKind: entity.Kind, EntityID: entity.ItemID ?? entity.AssemblyID,
        Code: entity.Code, Description: entity.Name,
        UOM: entity.UOM, MatchedInstanceCount: matchedCount, TotalQty: totalQty,
        DirectCost: directCost, SellValue: totalQty * computeUnitSellPrice(unitCost, entity, settings),
      });
    }
    return rows;
  }, [scopeApplications, scopeAppEntities, rooms, settings]);

  // Joins any rollup row (EntityKind/EntityID — takeoff-mapped or manual)
  // back to its full source Item/Assembly — to expose Category/
  // SalesBurdenPct/MarkupPct on the combined table without any backend change.
  const entityByKey = useMemo(() => {
    const map = new Map();
    for (const i of items) map.set(`item:${i.ItemID}`, i);
    for (const a of assemblies) map.set(`assembly:${a.AssemblyID}`, a);
    return map;
  }, [items, assemblies]);
  const laborFactorsById = useMemo(() => new Map(laborFactors.map(f => [f.FactorID, f])), [laborFactors]);
  const formulasById = useMemo(() => new Map(formulas.map(f => [f.FormulaID, f])), [formulas]);
  const lineItemsById = useMemo(() => new Map(lineItems.map(li => [li.LineItemID, li])), [lineItems]);

  // Manual line items (Source "M"), priced the same way the former
  // EstimateLineItemsPanel did (computeLineCost) and reshaped into the same
  // row shape as the takeoff-mapped rollup sources so they can share one
  // table and one grand total.
  const manualRollupRows = useMemo(() => {
    if (!settings) return [];
    return lineItems.map(li => {
      const entity = li.Kind === "item" ? entityByKey.get(`item:${li.ItemID}`) : entityByKey.get(`assembly:${li.AssemblyID}`);
      const laborFactor = li.LaborFactorID ? laborFactorsById.get(li.LaborFactorID) : laborFactorsById.get(entity?.DefaultLaborFactorID);
      // P3-C: a line may carry a price from evidence (CostSource / CarriedUnitCost) — computeLineCost honors it.
      const cost = computeLineCost({ Qty: li.Qty, CostSource: li.CostSource, CarriedUnitCost: li.CarriedUnitCost }, entity, laborFactor, settings, formulasById);
      const basis = resolveLineUnitCost(li, entity, laborFactor, settings);
      return {
        EntityKind: li.Kind, EntityID: li.Kind === "item" ? li.ItemID : li.AssemblyID,
        Code: entity?.Code, Description: li.Description || entity?.Description || entity?.ItemName || "—",
        UOM: entity?.UOM, MatchedInstanceCount: 1, TotalQty: cost.qty,
        DirectCost: cost.directCost, SellValue: cost.sellValue,
        Source: "M", LineItemID: li.LineItemID, AlternateID: li.AlternateID ?? null,
        carried: basis.carried, costSource: basis.source, basisText: describeBasis(li, basis), basisStale: basis.stale,
      };
    });
  }, [lineItems, entityByKey, laborFactorsById, formulasById, settings]);

  const activeRev = revisions.find(r => r.RevisionID === activeRevId);
  const isLocked = !!activeRev?.IsLocked;

  async function saveLineItem() {
    if (isLocked || !lineItemDraft) return;
    setSavingLineItem(true);
    try {
      const payload = {
        line_item_id: lineItemDraft.LineItemID, revision_id: activeRevId, kind: lineItemDraft.Kind, trade: lineItemDraft.Trade,
        item_id: lineItemDraft.Kind === "item" ? lineItemDraft.ItemID : null,
        assembly_id: lineItemDraft.Kind === "assembly" ? lineItemDraft.AssemblyID : null,
        description: lineItemDraft.Description || null, qty: +lineItemDraft.Qty || 1,
        labor_factor_id: lineItemDraft.LaborFactorID || null, notes: lineItemDraft.Notes || null,
        alternate_id: lineItemDraft.AlternateID || null,
        utility_type: lineItemDraft.UtilityType || null, // Phase H distribution allowance tag (always sent from the editor)
        // P3-C pricing basis — always sent from the editor (it shows it), so
        // switching back to "Catalog cost" clears the provenance explicitly.
        ...carriedPricingPayload({
          mode: lineItemDraft.CostSource || "catalog",
          quoteId: lineItemDraft.QuoteID, quoteLineItemId: lineItemDraft.QuoteLineItemID,
          sourceUnitCost: lineItemDraft.SourceUnitCost, carriedUnitCost: lineItemDraft.CarriedUnitCost, note: lineItemDraft.CarriedNote,
        }),
      };
      await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf(payload)}'`);
      setLineItemDraft(null);
      loadLineItems();
    } catch (e) { alert("Error saving line item: " + e.message); }
    finally { setSavingLineItem(false); }
  }

  async function removeLineItem(id) {
    if (!window.confirm("Remove this line item from the estimate?")) return;
    await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf({ line_item_id: id, revision_id: activeRevId, is_delete: true })}'`);
    loadLineItems();
  }

  // Type over a number on the Estimate: a takeoff line stores it in EstimateRollupOverrides (the takeoff and
  // catalog are never touched), a manual line stores it on the line itself. A blank box clears it.
  // An edit of one component of a component-built assembly (db/47): same five numbers as an Item's line, this estimate only.
  async function saveLeafOverride(edit, row, patch) {
    if (isLocked) return;
    const cols = { qty: "QtyOverride", unitCost: "UnitCostOverride", burden: "SalesBurdenPct", markup: "MarkupPct", unitPrice: "UnitPriceOverride" };
    const keys = { qty: "qty", unitCost: "unit_cost", burden: "sales_burden_pct", markup: "markup_pct", unitPrice: "unit_price" };
    const alt = row.AlternateID ?? 0, isCredit = (row.ParentQty || 0) < 0;
    const key = leafOverrideKey(edit.assemblyId, edit.childKind, edit.childId, alt, isCredit);
    const payload = { revision_id: activeRevId, assembly_id: edit.assemblyId, child_kind: edit.childKind, child_id: edit.childId, alternate_id: alt, is_credit: isCredit };
    const next = { AssemblyID: edit.assemblyId, ChildKind: edit.childKind, ChildID: edit.childId, AlternateID: alt, IsCredit: isCredit, QtyOverride: null, UnitCostOverride: null, SalesBurdenPct: null, MarkupPct: null, UnitPriceOverride: null, ...(assemblyLineOverrides.get(key) || {}) };
    for (const k of Object.keys(patch)) { payload[keys[k]] = patch[k]; next[cols[k]] = patch[k]; }
    setAssemblyLineOverrides(prev => { const m = new Map(prev); m.set(key, next); return m; }); // optimistic — the numbers move as you type
    try {
      const res = await sqlRead(`EXEC sp_SaveEstimateAssemblyLineOverride @payload = '${payloadOf(payload)}'`);
      const out = res?.Table1?.[0];
      if (out && out.Success === 0) { alert("Could not save: " + out.ErrorMessage); loadOverrides(); }
    } catch (e) { alert("Error saving the change: " + e.message); loadOverrides(); }
  }

  async function editRollupRow(r, patch, leaf) {
    if ("description" in patch) return editRowDescription(r, patch.description);
    if (r.Exploded) { // a split assembly line: the edit belongs to its single component, or to the one picked in the drill-down
      const edit = leaf ? { assemblyId: r.ParentID, childKind: leaf.childKind, childId: leaf.childId } : r.LeafEdit;
      if (edit) await saveLeafOverride(edit, r, patch);
      return;
    }
    if (isLocked || !r.editable) return;
    try {
      if (r.Source === "M") {
        const li = lineItemsById.get(r.LineItemID);
        if (!li) return;
        const payload = {
          line_item_id: li.LineItemID, revision_id: activeRevId, kind: li.Kind, trade: li.Trade,
          item_id: li.Kind === "item" ? li.ItemID : null, assembly_id: li.Kind === "assembly" ? li.AssemblyID : null,
          description: li.Description || null, qty: patch.qty != null ? patch.qty : li.Qty, labor_factor_id: li.LaborFactorID || null,
          notes: li.Notes || null, display_order: li.DisplayOrder, alternate_id: li.AlternateID || null,
        };
        if ("burden" in patch) payload.sales_burden_pct_override = patch.burden;
        if ("markup" in patch) payload.markup_pct_override = patch.markup;
        if ("unitPrice" in patch) payload.unit_price_override = patch.unitPrice;
        if ("unitCost" in patch) {
          const entity = entityByKey.get(`${li.Kind}:${li.Kind === "item" ? li.ItemID : li.AssemblyID}`);
          const laborFactor = li.LaborFactorID ? laborFactorsById.get(li.LaborFactorID) : laborFactorsById.get(entity?.DefaultLaborFactorID);
          Object.assign(payload, patch.unitCost == null
            ? carriedPricingPayload({ mode: "catalog" })
            : li.CostSource === "quote"
              ? carriedPricingPayload({ mode: "quote", quoteId: li.QuoteID, quoteLineItemId: li.QuoteLineItemID, sourceUnitCost: li.SourceUnitCost, carriedUnitCost: patch.unitCost, note: li.CarriedNote })
              : carriedPricingPayload({ mode: "manual", sourceUnitCost: li.CostSource === "manual" ? li.SourceUnitCost : catalogUnitCost(entity, laborFactor, settings), carriedUnitCost: patch.unitCost, note: li.CarriedNote }));
        }
        await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf(payload)}'`);
        loadLineItems();
        return;
      }
      const cols = { qty: "QtyOverride", unitCost: "UnitCostOverride", burden: "SalesBurdenPct", markup: "MarkupPct", unitPrice: "UnitPriceOverride" };
      const keys = { qty: "qty", unitCost: "unit_cost", burden: "sales_burden_pct", markup: "markup_pct", unitPrice: "unit_price" };
      const payload = { revision_id: activeRevId, entity_kind: r.EntityKind, entity_id: r.EntityID, alternate_id: r.AlternateID ?? 0, is_credit: r.overrideKey.endsWith(":cr") };
      const next = { EntityKind: r.EntityKind, EntityID: r.EntityID, AlternateID: r.AlternateID ?? 0, IsCredit: payload.is_credit, QtyOverride: null, UnitCostOverride: null, SalesBurdenPct: null, MarkupPct: null, UnitPriceOverride: null, ...(rollupOverrides.get(r.overrideKey) || {}) };
      for (const k of Object.keys(patch)) { payload[keys[k]] = patch[k]; next[cols[k]] = patch[k]; }
      setRollupOverrides(prev => { const m = new Map(prev); m.set(r.overrideKey, next); return m; }); // optimistic — the numbers move as you type
      const res = await sqlRead(`EXEC sp_SaveEstimateRollupOverride @payload = '${payloadOf(payload)}'`);
      const out = res?.Table1?.[0];
      if (out && out.Success === 0) { alert("Could not save: " + out.ErrorMessage); loadOverrides(); }
    } catch (e) { alert("Error saving the change: " + e.message); loadOverrides(); loadLineItems(); }
  }

  // Rename a line. A takeoff line's name is THIS estimate's name for the catalog type, so it changes the takeoff canvas
  // too (and back); a manual line's description lives on the line. A name equal to the catalog's, or blank, clears it.
  async function editRowDescription(r, text) {
    if (isLocked || !r.canRename) return;
    try {
      if (r.Source === "M") {
        const li = lineItemsById.get(r.LineItemID);
        if (!li) return;
        await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf({
          line_item_id: li.LineItemID, revision_id: activeRevId, kind: li.Kind, trade: li.Trade,
          item_id: li.Kind === "item" ? li.ItemID : null, assembly_id: li.Kind === "assembly" ? li.AssemblyID : null,
          description: text || null, qty: li.Qty, labor_factor_id: li.LaborFactorID || null,
          notes: li.Notes || null, display_order: li.DisplayOrder, alternate_id: li.AlternateID || null,
        })}'`);
        loadLineItems();
        return;
      }
      // a split assembly line of ONE component is named through that component's catalog row
      const nameKind = r.Exploded ? r.LeafEdit?.childKind : r.EntityKind, nameId = r.Exploded ? r.LeafEdit?.childId : r.EntityID;
      if (nameKind == null || nameId == null) return;
      const entity = entityByKey.get(`${nameKind}:${nameId}`);
      const catalogName = nameKind === "item" ? entity?.ItemName : entity?.Description;
      const name = text && text !== catalogName ? text : null;
      const key = `${nameKind}:${nameId}`;
      setNameOverrides(prev => { const m = new Map(prev); if (name) m.set(key, name); else m.delete(key); return m; });
      await sqlRead(`EXEC sp_SaveTakeoffConditionOverride @payload = '${payloadOf({
        revision_id: activeRevId, kind: nameKind,
        item_id: nameKind === "item" ? nameId : undefined, assembly_id: nameKind === "assembly" ? nameId : undefined,
        NameOverride: name,
      })}'`);
    } catch (e) { alert("Error renaming the line: " + e.message); loadOverrides(); loadLineItems(); }
  }

  async function resetRollupRow(r) {
    if (isLocked) return;
    if (r.Exploded) {
      if (r.descOverridden && r.LeafEdit) await editRowDescription(r, null);
      // The only thing that can be typed over a split assembly line is the quantity on the assembly's own line, from before it
      // had components — clear that one.
      // a quantity typed over the assembly's own line before it had components, and any edits to its components
      for (const part of r.Components || []) {
        if (part.override) await saveLeafOverride({ assemblyId: r.ParentID, childKind: part.childKind, childId: part.childId }, r, { qty: null, unitCost: null, burden: null, markup: null, unitPrice: null });
      }
      if (!r.parentQtyTyped) return;
      await editRollupRow({ ...r, EntityKind: "assembly", EntityID: r.ParentID, TotalQty: r.ParentQty, editable: true, overrideKey: `assembly:${r.ParentID}:${r.AlternateID ?? 0}:${(r.ParentQty || 0) < 0 ? "cr" : "add"}` }, { qty: null });
      return;
    }
    if (r.descOverridden) await editRowDescription(r, null);
    if (r.Source === "M") {
      const li = lineItemsById.get(r.LineItemID);
      if (!li) return;
      await editRollupRow(r, { burden: null, markup: null, unitPrice: null, ...(li.CostSource === "manual" ? { unitCost: null } : {}) });
    } else {
      await editRollupRow(r, { qty: null, unitCost: null, burden: null, markup: null, unitPrice: null });
    }
  }

  async function saveAlternate() {
    if (isLocked || !alternateDraft?.Name?.trim()) return null;
    setSavingAlternate(true);
    try {
      const result = await sqlRead(`EXEC sp_SaveEstimateLineItemAlternate @payload = '${payloadOf({
        revision_id: activeRevId, name: alternateDraft.Name.trim(), description: alternateDraft.Description || null,
      })}'`);
      const newId = result?.Table1?.[0]?.AlternateID ?? null;
      setAlternateDraft(null);
      loadAlternates();
      return newId;
    } catch (e) { alert("Error saving alternate: " + e.message); return null; }
    finally { setSavingAlternate(false); }
  }

  // The "Move to:" select on a manual row — reassigns AlternateID via the
  // same save path as any other edit, carrying every other field forward
  // unchanged (Base Scope is AlternateID "" / null in the <select>'s value).
  async function moveLineItemToAlternate(li, alternateId) {
    await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf({
      line_item_id: li.LineItemID, revision_id: activeRevId, kind: li.Kind, trade: li.Trade,
      item_id: li.Kind === "item" ? li.ItemID : null, assembly_id: li.Kind === "assembly" ? li.AssemblyID : null,
      description: li.Description || null, qty: li.Qty, labor_factor_id: li.LaborFactorID || null,
      notes: li.Notes || null, display_order: li.DisplayOrder, alternate_id: alternateId || null,
    })}'`);
    loadLineItems();
  }

  // "Duplicate & Credit Out" — inserts a negated copy of `row` (any source,
  // T or M) into the target alternate, then opens the manual-add form
  // pre-seeded to that alternate so the estimator can immediately pick the
  // replacement item. A credit is just an ordinary line with a negative Qty
  // — computeLineCost needs no special-casing for that (see project memory).
  async function confirmCredit(row) {
    let alternateId = creditTargetAlternateId ? +creditTargetAlternateId : null;
    if (!alternateId && creditNewAlternateName.trim()) {
      const result = await sqlRead(`EXEC sp_SaveEstimateLineItemAlternate @payload = '${payloadOf({
        revision_id: activeRevId, name: creditNewAlternateName.trim(),
      })}'`);
      alternateId = result?.Table1?.[0]?.AlternateID ?? null;
      loadAlternates();
    }
    if (!alternateId) { alert("Pick an existing alternate or name a new one first."); return; }

    const entity = entityByKey.get(`${row.EntityKind}:${row.EntityID}`);
    await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf({
      revision_id: activeRevId, kind: row.EntityKind, trade: entity?.Trade || "general",
      item_id: row.EntityKind === "item" ? row.EntityID : null, assembly_id: row.EntityKind === "assembly" ? row.EntityID : null,
      description: `Credit: ${row.Description}`, qty: -(row.TotalQty || 0), alternate_id: alternateId,
    })}'`);
    loadLineItems();
    setCreditPromptKey(null);
    setCreditTargetAlternateId("");
    setCreditNewAlternateName("");
    setLineItemDraft({ ...emptyLineItemDraft, AlternateID: alternateId });
  }

  // Process Utilities Phase H — utility connections priced off their mapped
  // connection / drop assembly (connection's own AssemblyID, else the catalog
  // default for the utility type), 1 per connection × installed units, and
  // plant / process equipment off each record's own cost mapping
  // (ProcessEquipment.CostKind/CostItemID/CostAssemblyID) × installed units;
  // electrical connections price through circuits. Scenarios Phase J: both are
  // composed per design-scenario disposition — the selected base's plant and
  // every scenario-independent record in Base Scope, a proposal alternate's
  // plant (+) and the base's replaced plant (−) under that alternate's header,
  // plant of an unpublished non-base scenario not priced and reported.
  const scenarioModel = useMemo(() => buildScenarioModel(equipmentRegister.scenarios, equipmentRegister.systems, equipmentRegister.systemZones), [equipmentRegister]);
  const scenarioDispositions = useMemo(() => scenarioEquipmentDispositions(scenarioModel, equipmentRegister.equipment, { alternates }), [scenarioModel, equipmentRegister, alternates]);
  const scenarioEquipment = useMemo(() => settings
    ? scenarioEstimateRows(scenarioDispositions, { equipment: equipmentRegister.equipment, connections: equipmentRegister.connections, items, assemblies, laborFactors, settings, rooms: roomsWithTemplateDefaults })
    : { rows: [], alternates: scenarioDispositions.alternates, excluded: scenarioDispositions.excluded, warnings: scenarioDispositions.warnings },
  [scenarioDispositions, equipmentRegister, items, assemblies, laborFactors, settings, roomsWithTemplateDefaults]);
  // Phase H-3 — the Phase G conceptual routes (design flow = Σ served, sizes
  // from the effective rule, LF from geometry / override) priced through the
  // catalog rows mapped to each material + nominal size ('utility_pipe') and
  // the per-LF allowances ('utility_pipe_lf'). Nothing is sized or measured here.
  const utilityRoutes = useMemo(() => {
    const dropCtx = { roomElementsByRoom: systemsCtx?.roomElementsByRoom, takeoffConditions, formulas };
    const { systems } = rollupUtilityDemand(equipmentRegister.equipment, equipmentRegister.connections, { rooms: roomsWithTemplateDefaults, criteria, ctx: dropCtx, systems: equipmentRegister.utilitySystems, tunables: systemsCtx?.tunables || {} });
    return evaluateUtilityRoutes({ systems, utilitySystems: equipmentRegister.utilitySystems, segments: equipmentRegister.routeSegments, config: sizingConfig || {}, tunables: systemsCtx?.tunables || {} }).routes;
  }, [equipmentRegister, roomsWithTemplateDefaults, criteria, systemsCtx, takeoffConditions, formulas, sizingConfig]);
  const distributionRollup = useMemo(() => settings
    ? utilityDistributionRollup({ routes: utilityRoutes, items, assemblies, laborFactors, settings }).rows
    : [], [utilityRoutes, items, assemblies, laborFactors, settings]);
  const utilityCoverageResult = useMemo(() => utilityCoverage({
    equipment: equipmentRegister.equipment, connections: equipmentRegister.connections, utilitySystems: equipmentRegister.utilitySystems,
    rooms: roomsWithTemplateDefaults, criteria, lineItems, items, assemblies, tunables: systemsCtx?.tunables || {},
    ctx: { roomElementsByRoom: systemsCtx?.roomElementsByRoom, takeoffConditions, formulas }, routes: utilityRoutes,
  }), [equipmentRegister, roomsWithTemplateDefaults, criteria, lineItems, items, assemblies, systemsCtx, takeoffConditions, formulas, utilityRoutes]);
  const utilityTypesPresent = useMemo(() => UTILITY_TYPES.filter(u => u.kind !== "electrical" && equipmentRegister.connections.some(c => c.UtilityType === u.key)), [equipmentRegister]);

  // A takeoff-derived row is Base Scope unless a scenario disposition put it
  // under a proposal alternate's header (scenarioEquipment rows carry AlternateID).
  // Scenarios Phase J-2 — the base's / proposal alternates' DISTRIBUTION
  // consequences (chilled-water pipe by material × size × LF, per-LF
  // allowances, refrigerant line LF) priced exactly as the comparison table
  // prices them (scenarioCostRollup) and placed in Base Scope or under the
  // alternate's header (base distribution credited there). The chain is the
  // Mechanical panel's own: zone loads (computeAhuZoneRollup) → scenario plant
  // chain (evaluateScenarios) → HVAC-owned routes (evaluateHvacRoutes).
  const ahuRollups = useMemo(() => {
    if (!settings || !zones.length) return [];
    const zonesById = Object.fromEntries(zones.map(z => [z.AHUZoneID, z]));
    // J-3: a non-base scenario's plant is not in the base design the zone loads describe.
    const heatByRoom = equipmentHeatGainByRoom(equipmentRegister.equipment, equipmentRegister.connections, { excludeEquipmentIds: nonBaseEquipmentIds(scenarioDispositions) }).byRoomBtuh;
    return computeAhuZoneRollup(roomsWithTemplateDefaults, criteria, criteria, settings, zonesById, heatByRoom, systemsCtx);
  }, [settings, zones, equipmentRegister, roomsWithTemplateDefaults, criteria, systemsCtx, scenarioDispositions]);
  const evaluatedScenarios = useMemo(() => scenarioModel.length ? evaluateScenarios(scenarioModel, { zones, ahuRollups, criteria, tunables: systemsCtx?.tunables || {} }) : [], [scenarioModel, zones, ahuRollups, criteria, systemsCtx]);
  const hvacRoutes = useMemo(() => evaluatedScenarios.length ? evaluateHvacRoutes({ scenarios: evaluatedScenarios, segments: equipmentRegister.routeSegments, config: sizingConfig || {}, utilitySystems: equipmentRegister.utilitySystems, tunables: systemsCtx?.tunables || {} }) : [], [evaluatedScenarios, equipmentRegister, sizingConfig]);
  const scenarioDistribution = useMemo(() => scenarioDistributionRows(evaluatedScenarios, hvacRoutes, scenarioDispositions, { equipment: equipmentRegister.equipment, items, assemblies, laborFactors, settings, utilityRoutes }),
    [evaluatedScenarios, hvacRoutes, scenarioDispositions, equipmentRegister, items, assemblies, laborFactors, settings, utilityRoutes]);

  // One line per catalog item: canvas-placed symbols + the not-yet-placed remainder of a room element merge (estimateRollupMerge.js).
  // Placed count symbols per drawing, for the merged line's breakdown (the pricing itself is unchanged and spans every drawing).
  const placedBySheet = new Map();
  for (const c of takeoffConditions) {
    const id = c.Kind === "item" ? c.ItemID : c.Kind === "assembly" ? c.AssemblyID : null;
    if (c.Style !== "count" || id == null || c.MeasureRole === "deduct") continue;
    const key = `${c.Kind}:${id}`;
    const label = c.SheetNumber || c.SheetName || "No drawing";
    const parts = placedBySheet.get(key) || placedBySheet.set(key, []).get(key);
    const part = parts.find(p => p.label === label) || (parts.push({ label, count: 0 }), parts[parts.length - 1]);
    part.count += 1;
  }
  // An assembly built from components shows one line per cost code its components sit under (or one line when it is set
  // to single-line). The split happens AFTER the drawn + element merge so placed and not-yet-placed quantity explode once.
  const mergedTakeoffRows = mergeDrawnAndElementRows([...rollup.map(r => ({ ...r, SourceDetail: "drawn" })), ...roomFormulaRollup, ...scopeApplicationRollup, ...roomElementsRollup, ...scenarioEquipment.rows, ...scenarioDistribution.rows, ...distributionRollup]
    .map(r => ({ ...r, Source: "T", AlternateID: r.AlternateID ?? null })), { placedBySheet });
  const { rows: explodedTakeoffRows, warnings: assemblyWarnings } = explodeRollupRows(mergedTakeoffRows, {
    componentsByAssembly, itemsById: componentItemsById, assembliesById: new Map(assemblies.map(a => [a.AssemblyID, a])),
    settings, laborFactorsById: new Map(laborFactors.map(f => [f.FactorID, f])), engineVars: assemblyEngineVars, libraryFormulas: formulas, leafOverrides: assemblyLineOverrides,
    qtyOverrideOf: (r) => { const v = rollupOverrides.get(overrideKeyOf(r))?.QtyOverride; return v == null ? null : Number(v); },
  });
  const approxManualAssemblies = [...new Set(manualRollupRows
    .filter(r => r.EntityKind === "assembly" && entityByKey.get(`assembly:${r.EntityID}`)?.HasFormulaComponents)
    .map(r => entityByKey.get(`assembly:${r.EntityID}`).Code))];
  const combinedRollup = explodedTakeoffRows
    .concat(manualRollupRows)
    .sort((a, b) => (a.Code || "").localeCompare(b.Code || ""));

  // Enrich each rollup row with its joined entity's Category/pricing-rate
  // fields, plus the Excel-style breakdown columns. Total stays exactly
  // SellValue (the app's one real pricing source of truth, computeUnitSellPrice)
  // — Total Direct Cost/Total Sales Burden/Total Cost are a decomposition of
  // that already-correct total, not an independent recalculation, so this
  // display can never disagree with what the estimate actually totals to
  // elsewhere. A row whose entity has no per-line SalesBurdenPct/MarkupPct
  // set prices via the whole-estimate sequential settings stack instead
  // (computeSellMultiplier) — shown as "Std." rather than a fabricated %.
  const sharedKeys = ambiguousKeys(combinedRollup.filter(r => r.Source === "T"));
  const enrichedRollup = combinedRollup.map(r => {
    // A split assembly line made of ONE component shows that component's own rates; a multi-component line is "Std." here
    // (each component is still priced by its own rules in the Total).
    const entity = r.Exploded ? (r.Components?.length === 1 ? r.Components[0].entity : undefined) : entityByKey.get(`${r.EntityKind}:${r.EntityID}`);
    // Working-estimate edits: a manual line carries its burden / markup on the line itself (its qty and
    // unit cost are already priced from the line); a takeoff line looks its numbers up in the override table.
    let ov = null, editable = r.EntityID != null, editBlockedReason;
    let overrideKey = null;
    const li = r.Source === "M" ? lineItemsById.get(r.LineItemID) : null;
    if (r.Source === "M") {
      editable = !!li;
      if (li) ov = { SalesBurdenPct: li.SalesBurdenPctOverride, MarkupPct: li.MarkupPctOverride, UnitPriceOverride: li.UnitPriceOverride };
    } else if (r.Exploded) {
      // Priced from the assembly's components — nothing to type over here; a quantity typed over the assembly's own line
      // before it had components still drives the split (explodeRollupRows), and Reset clears it.
      if (r.LeafEdit) { editable = true; ov = r.LeafOverride; } // one component on this line: edited like an Item (the edit is that component's)
      else { editable = false; editBlockedReason = "Several components share this line — open \"components\" and edit each one, or change the assembly on Setup ▸ Estimate Engine ▸ Assemblies."; }
    } else if (r.EntityID != null) {
      overrideKey = overrideKeyOf(r);
      if (sharedKeys.has(overrideKey)) { editable = false; editBlockedReason = "This item is fed by more than one source, so it cannot be edited here — change it at its source."; }
      else ov = rollupOverrides.get(overrideKey) || null;
    }
    // LF / SF is rounded to a whole number BEFORE pricing, so Qty x Unit price = the line total (utils/quantityRounding.js);
    // a quantity the estimator typed is used exactly as typed.
    let priced = r;
    if (!r.Exploded && r.Source === "T" && ov?.QtyOverride == null) {
      const q0 = +r.TotalQty, q1 = roundForPricing(q0, r.UOM);
      if (Number.isFinite(q0) && q0 !== 0 && q1 !== q0) { const f = q1 / q0; priced = { ...r, TotalQty: q1, DirectCost: r.DirectCost * f, SellValue: r.SellValue * f }; }
    }
    const o = r.Exploded ? r : applyRollupOverride(priced, ov, entity, settings); // a split line already carries its component edits
    const overridden = { ...(r.Exploded ? (r.LeafOverridden || {}) : (o.overridden || {})) };
    if (li) { overridden.burden = li.SalesBurdenPctOverride != null; overridden.markup = li.MarkupPctOverride != null; overridden.unitPrice = li.UnitPriceOverride != null; overridden.unitCost = li.CostSource === "manual"; }
    // Description: a takeoff line shows this estimate's name for its catalog type (shared with the takeoff engine);
    // a manual line carries its own description on the line.
    const nameOv = r.Source === "T" && r.EntityID != null ? nameOverrides.get(r.Exploded ? (r.LeafEdit ? `${r.LeafEdit.childKind}:${r.LeafEdit.childId}` : "") : `${r.EntityKind}:${r.EntityID}`) : null;
    const canRename = r.Source === "M" ? !!li : r.Exploded ? !!r.LeafEdit : r.EntityID != null;
    const parentQtyTyped = !!r.Exploded && rollupOverrides.get(`assembly:${r.ParentID}:${r.AlternateID ?? 0}:${(r.ParentQty || 0) < 0 ? "cr" : "add"}`)?.QtyOverride != null;
    if (parentQtyTyped) overridden.qty = true;
    const qty = o.TotalQty || 0;
    const total = lineTotal(o.SellValue); // Excel's Total column: =ROUND(Qty*Unit Price, 0)
    const unitCost = o.UnitCostEff ?? (qty !== 0 ? o.DirectCost / qty : 0);
    const unitPrice = qty !== 0 ? o.SellValue / qty : 0;
    const part1 = r.Exploded && r.LeafEdit ? r.Components[0] : null; // the single component's own effective rates
    const hasPerLineRates = part1 ? part1.hasRates : (!!entity && (entity.SalesBurdenPct != null || entity.MarkupPct != null)) || !!overridden.burden || !!overridden.markup || !!overridden.unitPrice;
    const salesBurdenPct = part1 ? part1.burdenPct : hasPerLineRates ? Number(ov?.SalesBurdenPct ?? entity?.SalesBurdenPct) || 0 : null;
    const markupPct = part1 ? part1.markupPct : hasPerLineRates ? (o.DerivedMarkupPct != null ? o.DerivedMarkupPct : Number(ov?.MarkupPct ?? entity?.MarkupPct) || 0) : null;
    const totalSalesBurden = hasPerLineRates ? o.DirectCost * (salesBurdenPct / 100) : 0;
    const totalCost = o.DirectCost + totalSalesBurden;
    return { ...o, Description: nameOv || o.Description, descOverridden: !!nameOv || (!!li && !!li.Description), canRename, SellValue: total, CSICode: entity?.CSICode ?? r.CSICode ?? null, CostCodeDescription: entity?.CostCodeDescription ?? r.CostCodeDescription ?? null, CategoryID: r.Exploded ? (r.CategoryID ?? entity?.CategoryID ?? null) : (entity?.CategoryID ?? r.CategoryID ?? null), parentQtyTyped, unitCost, unitPrice, hasPerLineRates, salesBurdenPct, markupPct, totalSalesBurden, totalCost, overridden, editable, editBlockedReason, overrideKey };
  });

  // Base Scope (AlternateID null) is grouped by Category and totaled exactly
  // as before Alternates existed — an alternate's own lines never affect
  // this number, matching how a real bid presents a firm base price plus
  // separately-priced options.
  const baseRows = enrichedRollup.filter(r => r.AlternateID == null);
  const altRows = enrichedRollup.filter(r => r.AlternateID != null);
  const rollupTotal = baseRows.reduce((sum, r) => sum + (r.SellValue || 0), 0);
  const baseTotals = baseRows.reduce((acc, r) => ({ directCost: acc.directCost + (r.DirectCost || 0), totalSalesBurden: acc.totalSalesBurden + (r.totalSalesBurden || 0), totalCost: acc.totalCost + (r.totalCost || 0) }), { directCost: 0, totalSalesBurden: 0, totalCost: 0 });
  const gpm = rollupTotal - baseTotals.totalCost; // gross profit margin ($): what the lines sell for less what they cost with sales burden
  const gpmPct = rollupTotal ? (gpm / rollupTotal) * 100 : null;
  const rollupRom = baseRows.reduce((sum, r) => sum + (romOf(r.SellValue) || 0), 0); // sum of the lines' ROMs, so the column adds up

  // Review gate: machine output (AI rooms, generated symbols, auto walls, template-seeded elements) that nobody
  // has reviewed yet. It still PRICES — this is visibility, so the estimate is never silently short and never
  // silently trusted. Value = quantity x the catalog row's unit sell price (a placeable element's remaining,
  // unplaced quantity only — its placed symbols are counted as symbols).
  const unitPriceByEntity = new Map();
  const splitAssemblySell = new Map(); // a split assembly's unit price = the sum of its per-cost-code lines per assembly unit
  for (const r of enrichedRollup) {
    if (r.Exploded) {
      const s = splitAssemblySell.get(r.ParentKey) || { sell: 0, qty: r.ParentQty };
      s.sell += r.SellValue || 0;
      splitAssemblySell.set(r.ParentKey, s);
      continue;
    }
    const k = `${r.EntityKind}:${r.EntityID}`;
    if (r.unitPrice > 0 && !unitPriceByEntity.has(k)) unitPriceByEntity.set(k, r.unitPrice);
  }
  for (const [k, s] of splitAssemblySell) if (s.qty && s.sell / s.qty > 0) unitPriceByEntity.set(k, s.sell / s.qty);
  const elementQtyById = new Map(roomElementsRollup.flatMap(r => r.Contributions || []).map(c => [c.elementId, c.qty]));
  const machineReview = summarizeUnreviewed({
    rooms: roomsWithTemplateDefaults, conditions: takeoffConditions, elements: roomElements,
    quantityOfCondition: (c) => computeConditionQuantities(c)[0]?.q ?? 0,
    quantityOfElement: (e) => elementQtyById.get(e.RoomElementID) ?? 0,
    unitPriceOf: (k) => unitPriceByEntity.get(k) || 0,
  });

  const groupedRollup = groupRollupByCategory(baseRows, categories);
  let rollupLineNo = 0;
  for (const g of groupedRollup) for (const r of g.rows) r.Line = ++rollupLineNo;

  const groupedAlternates = groupRollupByAlternate(altRows, alternates);
  for (const g of groupedAlternates) for (const r of g.rows) r.Line = ++rollupLineNo;

  // Export to Excel: hands the SAME rows / totals this page renders to the pure builder; no pricing is redone, nothing is written.
  function exportToExcel() {
    const now = new Date();
    const revisionLabel = activeRev ? `Rev ${activeRev.RevisionNumber}${activeRev.RevisionLabel ? ` — ${activeRev.RevisionLabel}` : ""}${activeRev.IsLocked ? " (locked)" : ""}` : "";
    const sheet = buildEstimateRows({
      projectName, revisionLabel, revisionId: activeRev?.RevisionID, exportedAt: now, projectSF,
      // same count text as the yellow banner; those items are priced, so say they are in the numbers
      machineNote: machineReview.counts.total > 0 ? `${describeUnreviewed(machineReview.counts)} — included in the numbers below, not yet reviewed.` : "",
      baseGroups: groupedRollup, altGroups: groupedAlternates, baseTotals, rollupTotal, rollupRom, gpm, gpmPct,
    });
    const bytes = buildEstimateXlsx(sheet, now);
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = estimateFileName({ project: projectName, revision: activeRev ? `Rev${activeRev.RevisionNumber}` : "", date: now });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // Early exits live BELOW every hook: React requires the same hooks in the
  // same order on every render, and `loading` flips true → false on the same
  // mounted component. (2026-09-14: fourteen useMemos added on 09-13 sat after
  // these returns and crashed the tab with "Rendered more hooks than during
  // the previous render".) Everything above is null-safe while loading.
  if (loading) return <div style={{ padding: 24, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;
  if (!revisions.length) return (
    <div style={{ padding: 24, color: "var(--text3)", fontSize: 13 }}>
      No estimate revision yet — create one on the Revisions tab first.
    </div>
  );
  return (
    <div className="est-sheet" style={{ fontFamily: SHEET_FONT }}>
      <style>{`.est-sheet input, .est-sheet select, .est-sheet button, .est-sheet textarea { font-family: inherit; }`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, marginRight: "auto" }}>💰 Estimate{typeof BroadcastChannel !== "undefined" && (
          <span data-testid="live-link" style={{ marginLeft: 10, fontSize: 11, fontWeight: 600, color: "#166534" }} title="Changes saved in the Takeoff canvas (another window of this browser) update this Estimate automatically">
            ● Live with the takeoff{liveUpdate ? ` · updated ${new Date(liveUpdate).toLocaleTimeString()}` : ""}
          </span>
        )}</h2>
        <select
          value={activeRevId || ""}
          onChange={e => setActiveRevId(+e.target.value)}
          style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--cs-border)" }}
        >
          {revisions.map(r => (
            <option key={r.RevisionID} value={r.RevisionID}>
              Rev {r.RevisionNumber} {r.RevisionLabel ? `— ${r.RevisionLabel}` : ""}{r.IsLocked ? " (locked)" : ""}
            </option>
          ))}
        </select>
      </div>

      {isLocked && (
        <div style={{ fontSize: 12, color: "#92400e", background: "var(--tint-amber)", padding: "6px 12px", borderRadius: 6, marginBottom: 12 }}>
          This revision is locked — read-only.
        </div>
      )}

      {assemblyWarnings.length > 0 && (
        <div data-testid="assembly-component-warnings" style={{ fontSize: 12, color: "#991b1b", background: "var(--tint-red)", border: "1px solid #fca5a5", padding: "6px 12px", borderRadius: 6, marginBottom: 12 }}>
          <strong>Assembly components need attention.</strong> These assemblies are priced as a single flat line until fixed:{" "}
          {[...new Map(assemblyWarnings.map(w => [`${w.assemblyCode}:${w.code}`, w])).values()].map(w => `${w.assemblyCode} (${w.message})`).join("; ")}
        </div>
      )}

      {approxManualAssemblies.length > 0 && (
        <div data-testid="assembly-manual-approx" style={{ fontSize: 12, color: "#92400e", background: "var(--tint-amber)", border: "1px solid #fcd34d", padding: "6px 12px", borderRadius: 6, marginBottom: 12 }}>
          <strong>Manual lines of assemblies with quantity formulas are approximate.</strong> {approxManualAssemblies.join(", ")} {approxManualAssemblies.length === 1 ? "has" : "have"} formula components (e.g. ceil), which only a takeoff line can evaluate exactly; a manual line prices from a per-unit roll-up.
        </div>
      )}

      {machineReview.counts.total > 0 && (
        <div data-testid="machine-review-flag" style={{ fontSize: 12, color: "#92400e", background: "var(--tint-amber)", border: "1px solid #fcd34d", padding: "6px 12px", borderRadius: 6, marginBottom: 12 }}>
          <strong>{describeUnreviewed(machineReview.counts)}</strong>
          {machineReview.valueAtStake > 0 && <> — about <strong>{fmt(machineReview.valueAtStake)}</strong> of this estimate prices them</>}.
          {" "}They are included in the numbers below; accept or correct them on Takeoff &amp; Design ▸ canvas ▸ Review ▸ Machine output.
        </div>
      )}

      {scenarioModel.length > 0 && (
        <div data-testid="scenario-scope" style={{ border: "1px solid var(--cs-border)", borderRadius: 8, padding: "8px 12px", marginBottom: 14, background: "var(--surface)" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>Design scenarios in this estimate</div>
          {scenarioDispositions.base ? (
            <div style={{ fontSize: 11.5 }}>
              Base Scope follows <strong>{scenarioDispositions.base.ScenarioKey} — {scenarioDispositions.base.Title}</strong> (selected base): its {scenarioDispositions.baseEquipmentIds.length} plant record(s) and every scenario-independent equipment record price here; the base also sets each served AHU zone's plant pointer.
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: "#92400e" }}>No selected base scenario — every plant record prices in Base Scope regardless of scenario (except proposal alternates). Select a base on Takeoff &amp; Design ▸ Mechanical ▸ Design scenarios.</div>
          )}
          {scenarioEquipment.alternates.map(a => (
            <div key={a.scenarioId} style={{ fontSize: 11.5, marginTop: 3 }} data-testid={`scenario-alternate-${a.scenarioKey}`}>
              <strong>{a.scenarioKey} — {a.title}</strong> → alternate {a.alternateId != null ? <em>{a.alternateName}</em> : <span style={{ color: "#b91c1c" }}>(no header yet)</span>}:
              {" "}adds {a.addedTags.length ? a.addedTags.join(", ") : "nothing"} · credits {a.creditedTags.length ? a.creditedTags.join(", ") : "nothing"} · {a.rowCount} live line(s){a.netSell != null ? ` · net ${a.netSell >= 0 ? "add" : "deduct"} ${fmt(Math.abs(a.netSell))}` : ""}
            </div>
          ))}
          {scenarioEquipment.warnings.filter(w => w.code !== "no_selected_base").map((w, i) => (
            <div key={`${w.code}-${w.equipmentId ?? w.scenarioKey ?? i}`} style={{ fontSize: 11.5, color: w.level === "info" ? "var(--text3)" : "#b91c1c", marginTop: 3 }} data-warning={w.code}>{w.message}</div>
          ))}
          {scenarioDistribution.scenarios.map(d => (
            <div key={`dist-${d.scenarioId}`} style={{ fontSize: 11.5, marginTop: 3 }} data-testid={`scenario-distribution-${d.scenarioKey}`}>
              Distribution {d.role === "base" ? "in Base Scope" : `under the ${d.alternateId != null ? "alternate" : "alternate (no header yet — not priced)"}`} for <strong>{d.scenarioKey}</strong>: {d.rowCount} live line(s){d.role === "alternate" && d.netSell != null ? ` · net ${d.netSell >= 0 ? "add" : "deduct"} ${fmt(Math.abs(d.netSell))} incl. the base distribution credit` : ""}
              {[...d.gaps, ...d.unresolved].map((g, i) => <div key={`${g.code}-${g.systemKey}-${i}`} style={{ color: "#b91c1c", marginLeft: 12 }} data-warning={g.code}>Not priced — {g.message}</div>)}
            </div>
          ))}
          {scenarioDistribution.warnings.map(w => <div key={w.code} style={{ fontSize: 11.5, color: "#b45309", marginTop: 3 }} data-warning={w.code}>{w.message}</div>)}
          {evaluatedScenarios.filter(sc => sc.isBase || sc.Status === "proposal_alternate").flatMap(sc => sc.systems.filter(h => h.plant?.dx?.reconcile).map(h => (
            <div key={`cu-${h.SystemID}`} style={{ fontSize: 11.5, color: "#92400e", marginTop: 3 }} data-warning="cu_count_differs_from_record">
              <strong>{sc.ScenarioKey} {h.SystemKey}</strong>: one condensing unit per AHU unit wants {h.plant.dx.reconcile.cuCount} CU; the record {h.plant.dx.reconcile.tag} is entered with Qty {h.plant.dx.reconcile.recordQty} and <strong>the estimate prices the record's Qty</strong>. Reconcile on Takeoff &amp; Design ▸ Mechanical ▸ Design scenarios ("Set Qty") or keep the record.
            </div>
          )))}
          {evaluatedScenarios.filter(sc => sc.review?.stale && (sc.isBase || sc.Status === "proposal_alternate")).map(sc => (
            <div key={`stale-${sc.ScenarioID}`} style={{ fontSize: 11.5, color: "#92400e", marginTop: 3 }} data-warning="scenario_stale">
              <strong>{sc.ScenarioKey}</strong> has changed since its review{sc.review.reviewedAt ? ` (${String(sc.review.reviewedAt).slice(0, 10)})` : ""}: {sc.review.reasons.map(x => x.message).join("; ")}. Its plant and routes price as they stand — re-review on Takeoff &amp; Design ▸ Mechanical ▸ Design scenarios.
            </div>
          ))}
          <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 4 }}>
            A record is claimed by a scenario when one of its HVAC systems names it as plant. Base-claimed and unclaimed records price in Base Scope; a proposal alternate prices its own plant as an add and the base plant it replaces as a credit under its alternate header (live, like a duplicate-and-credit); the plant of a draft / active-comparison / archived scenario is not priced while a base exists. Each record still prices × its installed Qty off its own cost mapping. The base's routed distribution (chilled-water pipe by material × size × LF, per-LF allowances, refrigerant line LF — Takeoff &amp; Design ▸ Mechanical ▸ Design scenarios routes) prices in Base Scope; an alternate prices its own and credits the base's. Segments still Sizing Required or without an LF are unpriced scope, never guessed.
          </div>
        </div>
      )}

      {(utilityCoverageResult.warnings.length > 0 || utilityCoverageResult.systems.length > 0) && (
        <div data-testid="utility-coverage" style={{ border: "1px solid var(--cs-border)", borderRadius: 8, padding: "8px 12px", marginBottom: 14, background: "var(--surface)" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>
            Process utility coverage
            <span style={{ fontWeight: 400, color: "var(--text3)", marginLeft: 8 }}>
              {utilityCoverageResult.systems.map(s => `${s.label}: ${({ allowance: "allowance", route: `route ${s.routePricedLF} LF priced`, route_partial: `route ${s.routePricedLF} LF priced (incomplete)`, "allowance+route": "allowance AND route — double basis" })[s.distributionBasis] || "no distribution basis"}`).join(" · ")}
            </span>
          </div>
          {utilityCoverageResult.warnings.length ? (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: "#92400e" }}>
              {utilityCoverageResult.warnings.map((w, i) => <li key={i} data-coverage={w.code}>{w.message}</li>)}
            </ul>
          ) : <div style={{ fontSize: 11.5, color: "#166534" }}>Every utility connection has a pricing mapping and every system with demand has a distribution basis (priced route or allowance).</div>}
          <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 4 }}>
            Connections price 1 per installed unit off the connection's assembly, else the catalog default for the utility (Setup ▸ Estimate Engine ▸ Takeoff mapping "Utility connection"); equipment prices × installed units off the record's own cost mapping (Assumed Equipment ▸ "Prices as"). Distribution prices the conceptual route's effective Material + Nominal size + LF (Assumed Equipment ▸ Distribution) through the catalog rows mapped "Utility pipe" per material + size and "Utility pipe per-LF allowance" (hangers / supports / insulation / fittings, per sized LF) — nothing is sized or measured here; segments still Sizing Required or without an LF are unpriced scope. An estimator allowance line (tag it above) is the alternative basis, never both.
          </div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Base Scope</div>
        <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0 }}>
          One complete estimate — every Item/Assembly mapped to a takeoff source ("T", recomputed live from the current
          takeoff every time this page is viewed) plus every manual line item ("M", added below for scope that doesn't
          fit a takeoff-mapped pattern). Only the "M" rows are saved directly; the "T" rows are never stored.
        </p>

        {/* Read-only export of what is on screen — the rows above are handed over as computed; nothing is saved. */}
        <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
          {!isLocked && (<>
            <button onClick={() => setLineItemDraft({ ...emptyLineItemDraft })} style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>+ Add to Estimate</button>
            <button onClick={() => setAlternateDraft({ Name: "", Description: "" })} style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>+ New Alternate</button>
          </>)}
          <button onClick={exportToExcel} disabled={!baseRows.length && !altRows.length} title="Download this estimate, as shown, as an Excel file (AI-assisted draft — estimator review required)"
            style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", background: "var(--surface)", color: "var(--cs-blue)", border: "1px solid var(--cs-blue)", borderRadius: 6, cursor: !baseRows.length && !altRows.length ? "not-allowed" : "pointer", opacity: !baseRows.length && !altRows.length ? 0.45 : 1 }}>⬇ Export to Excel</button>
        </div>

        {alternateDraft && (
          <div style={{ display: "flex", gap: 8, padding: 12, border: "1px solid var(--cs-border)", borderRadius: 8, marginBottom: 12, background: "var(--surface)" }}>
            <input placeholder="Alternate name (e.g. Grid Ceiling in lieu of Walkable Ceiling)" value={alternateDraft.Name} onChange={e => setAlternateDraft({ ...alternateDraft, Name: e.target.value })} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, flex: 2 }} />
            <input placeholder="Description (optional)" value={alternateDraft.Description} onChange={e => setAlternateDraft({ ...alternateDraft, Description: e.target.value })} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, flex: 3 }} />
            <button onClick={saveAlternate} disabled={savingAlternate || !alternateDraft.Name.trim()} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{savingAlternate ? "Saving…" : "Save"}</button>
            <button onClick={() => setAlternateDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
          </div>
        )}

        {lineItemDraft && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, padding: 12, border: "1px solid var(--cs-border)", borderRadius: 8, marginBottom: 12, background: "var(--surface)" }}>
            {lineItemDraft.AlternateID && (
              <div style={{ gridColumn: "span 4", fontSize: 11, color: "var(--cs-blue)", fontWeight: 600 }}>
                Adding to alternate: {alternates.find(a => a.AlternateID === lineItemDraft.AlternateID)?.Name || "—"}
              </div>
            )}
            <select value={lineItemDraft.Trade} onChange={e => setLineItemDraft({ ...lineItemDraft, Trade: e.target.value })} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 }}>
              {TRADES.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
            </select>
            {/* Phase H — an explicit distribution allowance for a utility system; the only distribution pricing basis until Phase G routes exist */}
            <select aria-label="Utility distribution allowance" value={lineItemDraft.UtilityType || ""} onChange={e => setLineItemDraft({ ...lineItemDraft, UtilityType: e.target.value || null })}
              title="Tag this line as the distribution allowance for a process utility system — it counts as that system's distribution pricing basis in the coverage below"
              style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 }}>
              <option value="">Not a utility distribution allowance</option>
              {utilityTypesPresent.map(u => <option key={u.key} value={u.key}>Distribution allowance — {u.label}</option>)}
            </select>
            <div style={{ gridColumn: "span 3" }}>
              <ItemAssemblyPicker
                items={items} assemblies={assemblies} categories={categories}
                value={lineItemDraft.Kind && (lineItemDraft.ItemID || lineItemDraft.AssemblyID) ? { kind: lineItemDraft.Kind, id: lineItemDraft.Kind === "item" ? lineItemDraft.ItemID : lineItemDraft.AssemblyID } : null}
                onSelect={e => setLineItemDraft({ ...lineItemDraft, Kind: e.kind, ItemID: e.kind === "item" ? e.id : null, AssemblyID: e.kind === "assembly" ? e.id : null })}
                onRequestAddNew={() => setShowAddNewEntity(true)}
              />
            </div>
            <input type="number" step="0.01" placeholder="Qty" value={lineItemDraft.Qty} onChange={e => setLineItemDraft({ ...lineItemDraft, Qty: e.target.value })} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, boxSizing: "border-box" }} />
            <input placeholder="Description override (optional)" value={lineItemDraft.Description} onChange={e => setLineItemDraft({ ...lineItemDraft, Description: e.target.value })} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, boxSizing: "border-box", gridColumn: "span 2" }} />
            <select value={lineItemDraft.LaborFactorID || ""} onChange={e => setLineItemDraft({ ...lineItemDraft, LaborFactorID: e.target.value ? +e.target.value : null })} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 }}>
              <option value="">Labor factor (default) —</option>
              {laborFactors.map(f => <option key={f.FactorID} value={f.FactorID}>{f.Code}</option>)}
            </select>
            <input placeholder="Notes" value={lineItemDraft.Notes} onChange={e => setLineItemDraft({ ...lineItemDraft, Notes: e.target.value })} style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, boxSizing: "border-box", gridColumn: "span 3" }} />
            {/* P3-C — Pricing basis: catalog (live) | a project quote line | manual. Source cost is frozen when carried; the carried cost is what prices. */}
            {(() => {
              const draftEntity = lineItemDraft.Kind === "item" ? entityByKey.get(`item:${lineItemDraft.ItemID}`) : entityByKey.get(`assembly:${lineItemDraft.AssemblyID}`);
              const draftFactor = lineItemDraft.LaborFactorID ? laborFactorsById.get(lineItemDraft.LaborFactorID) : laborFactorsById.get(draftEntity?.DefaultLaborFactorID);
              const catalogNow = settings ? catalogUnitCost(draftEntity, draftFactor, settings) : 0;
              const mode = lineItemDraft.CostSource || "catalog";
              const inputStyle = { fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, boxSizing: "border-box" };
              const adj = lineItemDraft.CarriedUnitCost != null && lineItemDraft.SourceUnitCost != null && lineItemDraft.CarriedUnitCost !== "" ? +lineItemDraft.CarriedUnitCost - +lineItemDraft.SourceUnitCost : null;
              return (
                <div style={{ gridColumn: "span 4", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, padding: 8, border: "1px dashed var(--cs-border)", borderRadius: 6 }}>
                  <div style={{ gridColumn: "span 4", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Pricing basis <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>· catalog cost now {fmt(catalogNow)}/unit</span>
                  </div>
                  <select aria-label="Pricing basis" value={mode} style={inputStyle}
                    onChange={e => {
                      const m = e.target.value;
                      if (m === "catalog") setLineItemDraft({ ...lineItemDraft, CostSource: "catalog", QuoteID: null, QuoteLineItemID: null, SourceUnitCost: null, CarriedUnitCost: null, CarriedNote: "" });
                      else if (m === "manual") setLineItemDraft({ ...lineItemDraft, CostSource: "manual", QuoteID: null, QuoteLineItemID: null, SourceUnitCost: catalogNow, CarriedUnitCost: lineItemDraft.CarriedUnitCost ?? catalogNow });
                      else setLineItemDraft({ ...lineItemDraft, CostSource: "quote", CarriedUnitCost: lineItemDraft.QuoteID ? lineItemDraft.CarriedUnitCost : null });
                    }}>
                    {COST_SOURCES.map(s => <option key={s.key} value={s.key} title={s.description}>{s.label}</option>)}
                  </select>
                  {mode === "quote" && (
                    <select aria-label="Quote line" value={lineItemDraft.QuoteLineItemID ?? ""} style={{ ...inputStyle, gridColumn: "span 3" }}
                      onChange={e => {
                        const o = quoteLinePickerOptions.find(x => String(x.quoteLineItemId) === e.target.value);
                        if (o) setLineItemDraft({ ...lineItemDraft, QuoteID: o.quoteId, QuoteLineItemID: o.quoteLineItemId, SourceUnitCost: o.unitPrice, CarriedUnitCost: o.unitPrice });
                        else setLineItemDraft({ ...lineItemDraft, QuoteID: null, QuoteLineItemID: null, SourceUnitCost: null, CarriedUnitCost: null });
                      }}>
                      <option value="">{quoteLinePickerOptions.length ? "— pick a quote line from this project —" : "— no priced quote lines on this project (Quote Log) —"}</option>
                      {quoteLinePickerOptions.map(o => <option key={o.quoteLineItemId} value={o.quoteLineItemId}>{o.label}</option>)}
                    </select>
                  )}
                  {mode !== "catalog" && (
                    <>
                      <label style={{ fontSize: 11.5, display: "flex", flexDirection: "column", gap: 2 }}>Source unit cost (frozen)
                        <input aria-label="Source unit cost" type="number" step="0.01" readOnly value={lineItemDraft.SourceUnitCost ?? ""} style={{ ...inputStyle, background: "var(--surface2, #f8fafc)" }} />
                      </label>
                      <label style={{ fontSize: 11.5, display: "flex", flexDirection: "column", gap: 2 }}>Carried unit cost (prices the line)
                        <input aria-label="Carried unit cost" type="number" step="0.01" value={lineItemDraft.CarriedUnitCost ?? ""} onChange={e => setLineItemDraft({ ...lineItemDraft, CarriedUnitCost: e.target.value === "" ? null : +e.target.value })} style={inputStyle} />
                      </label>
                      <label style={{ fontSize: 11.5, display: "flex", flexDirection: "column", gap: 2, gridColumn: "span 2" }}>Why adjusted / note{adj ? ` (adjustment ${adj > 0 ? "+" : "−"}${fmt(Math.abs(adj))})` : ""}
                        <input aria-label="Carried note" value={lineItemDraft.CarriedNote || ""} onChange={e => setLineItemDraft({ ...lineItemDraft, CarriedNote: e.target.value })} placeholder={mode === "quote" ? "e.g. + freight, quote excludes it" : "why this number"} style={inputStyle} />
                      </label>
                    </>
                  )}
                </div>
              );
            })()}
            <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
              <button
                onClick={saveLineItem} disabled={savingLineItem || (lineItemDraft.Kind === "assembly" ? !lineItemDraft.AssemblyID : !lineItemDraft.ItemID)}
                style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}
              >
                {savingLineItem ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setLineItemDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
            </div>
          </div>
        )}

        <ContextMenu menu={rowMenu} onClose={() => setRowMenu(null)} />
        {substituteRow && (() => {
          const kind = substituteRow.Exploded ? substituteRow.ParentKind : substituteRow.EntityKind;
          const id = substituteRow.Exploded ? substituteRow.ParentID : substituteRow.EntityID;
          const ent = entityByKey.get(`${kind}:${id}`);
          return (
            <SubstituteItemDialog
              old={{ kind, id, name: (kind === "item" ? ent?.ItemName : ent?.Description) || substituteRow.Description, uom: ent?.UOM || substituteRow.UOM }}
              revisionId={activeRevId} items={items} assemblies={assemblies} categories={categories}
              onClose={() => setSubstituteRow(null)}
              onDone={() => {
                setSubstituteRow(null);
                loadRollup(); loadLineItems(); loadRoomElements(); loadScopeApplications(); loadRoomsAndCriteria(); loadOverrides();
                try { const bc = new BroadcastChannel(takeoffChannelName(activeRevId)); bc.postMessage({ from: "estimate", at: Date.now() }); bc.close(); } catch { /* no live link */ }
              }}
            />
          );
        })()}

        {showAddNewEntity && (
          <AddItemOrAssemblyFlyout
            categories={categories}
            onClose={() => setShowAddNewEntity(false)}
            onSaved={e => {
              setLineItemDraft(d => d ? { ...d, Kind: e.kind, ItemID: e.kind === "item" ? e.id : null, AssemblyID: e.kind === "assembly" ? e.id : null } : d);
              handleEntityCreated(e);
            }}
          />
        )}

        {rollupLoading ? (
          <div style={{ padding: 12, color: "var(--text3)", fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th} title="Cost code (CSI)">CSI Code</th><th style={th} title="Takeoff-mapped or Manual">Src</th><th style={th}>Line</th><th style={th}>Code</th><th style={th}>Description</th><th style={thR}>Qty</th><th style={th}>Unit</th>
                <th style={thR}>Unit Cost</th><th style={thR}>Total Direct Cost</th>
                <th style={thR}>Sales Burden %</th><th style={thR}>Total Sales Burden</th><th style={thR}>Total Cost</th>
                <th style={thR}>Mark Up %</th><th style={thR}>Unit Price</th><th style={thR}>Total</th><th style={thR} title="Total rounded up to the next $100">ROM</th><th style={thR} title="Total ÷ project SF (Bid Package)">$/SF</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {groupedRollup.map(g => {
                  const collapsed = collapsedRollupGroups.has(g.key);
                  const groupTotals = g.rows.reduce((acc, r) => {
                    acc.directCost += r.DirectCost || 0;
                    acc.totalSalesBurden += r.totalSalesBurden || 0;
                    acc.totalCost += r.totalCost || 0;
                    acc.total += r.SellValue || 0;
                    acc.rom += romOf(r.SellValue) || 0;
                    return acc;
                  }, { directCost: 0, totalSalesBurden: 0, totalCost: 0, total: 0, rom: 0 });
                  return (
                    <Fragment key={g.key}>
                      <tr onClick={() => toggleRollupGroup(g.key)} style={{ cursor: "pointer" }}>
                        <td style={{ ...td, ...bandCell }} colSpan={8}>
                          <span style={{ display: "inline-block", marginRight: 6, transition: "transform 0.1s", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▾</span>
                          {g.label} <span style={{ fontWeight: 400, opacity: 0.7 }}>({g.rows.length})</span>
                        </td>
                        <td style={{ ...td, ...bandCell }}><Acct v={groupTotals.directCost} /></td>
                        <td style={{ ...td, ...bandCell }}></td>
                        <td style={{ ...td, ...bandCell }}><Acct v={groupTotals.totalSalesBurden} /></td>
                        <td style={{ ...td, ...bandCell }}><Acct v={groupTotals.totalCost} /></td>
                        <td style={{ ...td, ...bandCell }}></td>
                        <td style={{ ...td, ...bandCell }}></td>
                        <td style={{ ...td, ...bandCell }}><Acct v={groupTotals.total} /></td>
                        <td style={{ ...td, ...bandCell }}><Acct v={groupTotals.rom} /></td>
                        <td style={{ ...td, ...bandCell }}><Acct v={perSf(groupTotals.total, projectSF)} /></td>
                        <td style={{ ...td, ...bandCell }}></td>
                      </tr>
                      {!collapsed && g.rows.map(r => (
                        <RollupRow
                          key={`${r.Source}-${r.SourceDetail ?? ""}-${r.EntityKind}-${r.EntityID}-${r.LineItemID ?? ""}-${r.AlternateID ?? ""}-${(r.TotalQty || 0) < 0 ? "cr" : "add"}`}
                          r={r} rowKey={`${r.Source}-${r.SourceDetail ?? ""}-${r.EntityKind}-${r.EntityID}-${r.LineItemID ?? ""}-${r.AlternateID ?? ""}-${(r.TotalQty || 0) < 0 ? "cr" : "add"}`}
                          isLocked={isLocked} alternates={alternates} lineItemsById={lineItemsById}
                          onEdit={setLineItemDraft} onRemove={removeLineItem} onMove={moveLineItemToAlternate} onEditRow={editRollupRow} onResetRow={resetRollupRow} projectSF={projectSF} onSubstitute={isLocked ? null : setSubstituteRow} onRowMenu={setRowMenu}
                          creditPromptKey={creditPromptKey} onToggleCreditPrompt={toggleCreditPrompt}
                          creditTargetAlternateId={creditTargetAlternateId} setCreditTargetAlternateId={setCreditTargetAlternateId}
                          creditNewAlternateName={creditNewAlternateName} setCreditNewAlternateName={setCreditNewAlternateName}
                          onConfirmCredit={confirmCredit}
                        />
                      ))}
                    </Fragment>
                  );
                })}
                {!baseRows.length && (
                  <tr><td style={td} colSpan={18}>
                    No base scope line items yet — set up a Takeoff Mapping from the Estimate Library (Electrical → Assembly Library) or Condition Library, or "+ Add to Estimate" above for scope that doesn't fit a takeoff-mapped pattern.
                  </td></tr>
                )}
              </tbody>
              {!!baseRows.length && (
                <tfoot><tr style={{ fontWeight: 700 }}>
                  <td style={{ ...td, ...bandCell }} colSpan={8}>Base Scope Total</td>
                  <td style={{ ...td, ...bandCell }}><Acct v={baseTotals.directCost} /></td>
                  <td style={{ ...td, ...bandCell }}></td>
                  <td style={{ ...td, ...bandCell }}><Acct v={baseTotals.totalSalesBurden} /></td>
                  <td style={{ ...td, ...bandCell }}><Acct v={baseTotals.totalCost} /></td>
                  <td style={{ ...td, ...bandCell }}></td>
                  <td style={{ ...td, ...bandCell }}></td>
                  <td style={{ ...td, ...bandCell }}><Acct v={rollupTotal} /></td>
                  <td style={{ ...td, ...bandCell }}><Acct v={rollupRom} /></td>
                  <td style={{ ...td, ...bandCell }}><Acct v={perSf(rollupTotal, projectSF)} /></td>
                  <td style={{ ...td, ...bandCell }}></td>
                </tr></tfoot>
              )}
            </table>
          </div>
        )}

        {!!groupedAlternates.length && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Alternates</div>
            <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th} title="Cost code (CSI)">CSI Code</th><th style={th} title="Takeoff-mapped or Manual">Src</th><th style={th}>Line</th><th style={th}>Code</th><th style={th}>Description</th><th style={thR}>Qty</th><th style={th}>Unit</th>
                  <th style={thR}>Unit Cost</th><th style={thR}>Total Direct Cost</th>
                  <th style={thR}>Sales Burden %</th><th style={thR}>Total Sales Burden</th><th style={thR}>Total Cost</th>
                  <th style={thR}>Mark Up %</th><th style={thR}>Unit Price</th><th style={thR}>Total</th><th style={thR} title="Total rounded up to the next $100">ROM</th><th style={thR} title="Total ÷ project SF (Bid Package)">$/SF</th><th style={th}></th>
                </tr></thead>
                <tbody>
                  {groupedAlternates.map(g => {
                    const collapsed = collapsedRollupGroups.has(`alt-${g.key}`);
                    const netTotal = g.rows.reduce((sum, r) => sum + (r.SellValue || 0), 0);
                    return (
                      <Fragment key={g.key}>
                        <tr onClick={() => toggleRollupGroup(`alt-${g.key}`)} style={{ cursor: "pointer" }}>
                          <td style={{ ...td, ...bandCell }} colSpan={14}>
                            <span style={{ display: "inline-block", marginRight: 6, transition: "transform 0.1s", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▾</span>
                            {g.label} <span style={{ fontWeight: 400, color: "var(--text3)" }}>({g.rows.length})</span>
                          </td>
                          <td style={{ ...td, ...bandCell }}>{netTotal >= 0 ? "Add" : "Deduct"}: {fmt(netTotal)}</td>
                          <td style={{ ...td, background: SHEET_BAND }}></td>
                          <td style={{ ...td, background: SHEET_BAND }}></td>
                          <td style={{ ...td, background: SHEET_BAND }}></td>
                        </tr>
                        {!collapsed && g.rows.map(r => (
                          <RollupRow
                            key={`${r.Source}-${r.SourceDetail ?? ""}-${r.EntityKind}-${r.EntityID}-${r.LineItemID ?? ""}-${r.AlternateID ?? ""}-${(r.TotalQty || 0) < 0 ? "cr" : "add"}`}
                            r={r} rowKey={`${r.Source}-${r.SourceDetail ?? ""}-${r.EntityKind}-${r.EntityID}-${r.LineItemID ?? ""}-${r.AlternateID ?? ""}-${(r.TotalQty || 0) < 0 ? "cr" : "add"}`}
                            isLocked={isLocked} alternates={alternates} lineItemsById={lineItemsById}
                            onEdit={setLineItemDraft} onRemove={removeLineItem} onMove={moveLineItemToAlternate} onEditRow={editRollupRow} onResetRow={resetRollupRow} projectSF={projectSF} onSubstitute={isLocked ? null : setSubstituteRow} onRowMenu={setRowMenu}
                            creditPromptKey={creditPromptKey} onToggleCreditPrompt={toggleCreditPrompt}
                            creditTargetAlternateId={creditTargetAlternateId} setCreditTargetAlternateId={setCreditTargetAlternateId}
                            creditNewAlternateName={creditNewAlternateName} setCreditNewAlternateName={setCreditNewAlternateName}
                            onConfirmCredit={confirmCredit}
                          />
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!!baseRows.length && (
          <div data-testid="estimate-summary" style={{ marginTop: 14, padding: "8px 12px", display: "flex", gap: 22, flexWrap: "wrap", alignItems: "baseline", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 8, background: "var(--surface)" }}>
            <span title="Bid Package ▸ Project SF"><b>{projectSF ? projectSF.toLocaleString("en-US") : "—"}</b> <span style={{ color: "var(--text3)" }}>Project SF</span></span>
            <span title="Base Scope Total"><b>{fmt(rollupTotal)}</b> <span style={{ color: "var(--text3)" }}>Base Scope Total</span></span>
            <span title="Total Cost column: direct cost plus sales burden"><b>{fmt(baseTotals.totalCost)}</b> <span style={{ color: "var(--text3)" }}>Total Cost</span></span>
            <span title="Base Scope Total − Total Cost"><b>{fmt(gpm)}</b> <span style={{ color: "var(--text3)" }}>GPM</span></span>
            <span title="GPM ÷ Base Scope Total"><b>{gpmPct == null ? "—" : `${gpmPct.toFixed(1)}%`}</b> <span style={{ color: "var(--text3)" }}>GPM %</span></span>
            <span style={{ color: "var(--text3)", marginLeft: "auto" }}>Base Scope only · AI-assisted draft — estimator review required</span>
          </div>
        )}
      </div>
    </div>
  );
}
